import React, { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../components/common/Toast';
import { PageHeader } from '../../components/common/PageHeader';
import { Modal } from '../../components/common/Modal';
import { UserProfile, Gelanggang, BengkungLevel, Role } from '../../types';
import { getMembersDB, saveMemberDB, saveInvoiceDB, deleteMemberDB, syncAllMembersToTursoDB, getNextMemberId, refreshMembersLiveDB, getBengkungRanksDB } from '../../services/db';
import { createOnboardingInvoice } from '../../utils/pricing';
import { saveViewScroll, restoreViewScroll, resetScrollToTop } from '../../utils/scrollRestoration';
import {
  Users,
  User,
  Search,
  Award,
  UserPlus,
  CheckCircle2,
  Eye,
  Edit3,
  Trash2,
  Phone,
  Mail,
  Calendar,
  MapPin,
  Shield,
  Lock,
  Database,
  Clock,
  Check,
  X,
  Copy,
  ExternalLink,
  Filter,
  ArrowUpDown,
  UserCheck,
  UserX,
  MessageCircle,
  Building,
  Home,
  CheckSquare,
  Square,
  AlertCircle,
  Info,
  Sparkles,
  ChevronRight,
  ShieldCheck,
  HeartHandshake,
  Navigation,
  History,
  RotateCcw,
  FileText,
  ArrowLeft,
  Camera,
  Loader2
} from 'lucide-react';
import { uploadMemberPhotoToAhliDriveJson } from '../../lib/gdrive';
import { BengkungCulaBadge } from '../../components/common/BengkungCulaBadge';
import { scoreMemberFuzzy } from '../../components/common/MemberSelectCombobox';
import { cleanBengkungTitle, getSilatRankFromBengkung, getCustomBengkungRanks, BengkungRankInfo } from '../../data/bengkungRanks';
import { isValidEmail, sanitizeIc, sanitizePhone, sanitizeMemberId, isSuperAdmin, isManagementOrTrainer, isJurulatih, isPengurusanRole, isUserManagement, canViewMemberSensitiveInfo, getIcDetails, calculateAge, isChildOfParent, findDuplicateIcMember, findDuplicatePhoneMember, toTitleCase } from '../../utils/formatters';

const getFormattedBengkungOption = (rank: BengkungRankInfo): string => {
  return rank.rankTitle ? `${rank.code} (${rank.rankTitle})` : rank.code;
};

const getResolvedBengkungValue = (currentVal: string | undefined, ranks: BengkungRankInfo[]): string => {
  if (!currentVal) return ranks[0] ? getFormattedBengkungOption(ranks[0]) : 'Putih (Pesilat Asas)';

  // 1. Direct match with formatted string or code or dbCode
  const directMatch = ranks.find(r =>
    getFormattedBengkungOption(r) === currentVal ||
    r.code === currentVal ||
    r.dbCode === currentVal
  );
  if (directMatch) return getFormattedBengkungOption(directMatch);

  // 2. Normalized alphanumeric match e.g. "kuning5" -> matches dbCode "kuning5" or code "kuning 5"
  const clean = currentVal.replace(/\s*\([^)]*\)/g, '').toLowerCase().replace(/[^a-z0-9_]/g, '');
  const matched = ranks.find(r =>
    r.dbCode === clean ||
    (clean === 'kuning' && r.dbCode === 'kuning0') ||
    (clean === 'putih' && r.dbCode === 'putih0') ||
    (clean === 'hitam' && r.dbCode === 'hitam0') ||
    (clean === 'merah' && r.dbCode === 'merah0') ||
    r.code.toLowerCase().replace(/[^a-z0-9_]/g, '') === clean
  );
  if (matched) return getFormattedBengkungOption(matched);

  // 3. Fallback for legacy Kuning titles like "Kuning 1 (Jurulatih Darjah 1)" or "Kuning (Penolong Jurulatih)"
  if (currentVal.toLowerCase().includes('kuning')) {
    const num = currentVal.replace(/[^0-9]/g, '') || '0';
    const targetDb = `kuning${num}`;
    const kuningMatch = ranks.find(r => r.dbCode === targetDb);
    if (kuningMatch) return getFormattedBengkungOption(kuningMatch);
  }

  return currentVal;
};

export type DirectorySortOption =
  | 'default'
  | 'id_asc'
  | 'id_desc'
  | 'age_desc'
  | 'age_asc'
  | 'bengkung_desc'
  | 'bengkung_asc'
  | 'name'
  | 'name_desc'
  | 'oldest'
  | 'newest';

export const MemberDirectoryPage: React.FC = () => {
  const { selectedGelanggang, currentUser, setCurrentUser } = useAuth();
  const { showToast } = useToast();

  // Live Bengkung Ranks hierarchy loaded dynamically from /settings/bengkung
  const [bengkungRanksList, setBengkungRanksList] = useState<BengkungRankInfo[]>(() => getCustomBengkungRanks());

  // Instant 0ms cache initialization for frame 1 rendering
  const [members, setMembers] = useState<UserProfile[]>(() => {
    try {
      const raw = localStorage.getItem('asmm_db_members_v4');
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed.map((m: UserProfile) => ({
        ...m,
        gelanggang: m.gelanggang === 'Semua Gelanggang' ? 'ASMM' : m.gelanggang
      })) : [];
    } catch {
      return [];
    }
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [bengkungFilter, setBengkungFilter] = useState<string>('all');

  const getRoleFilterFromUrl = (): string => {
    if (typeof window !== 'undefined') {
      const path = window.location.pathname.toLowerCase();
      const params = new URLSearchParams(window.location.search);
      const qRole = params.get('role') || params.get('peranan');
      if (qRole && ['all', 'pengurusan', 'ahli', 'waris'].includes(qRole.toLowerCase())) {
        return qRole.toLowerCase();
      }

      if (
        path.startsWith('/members/daftar') ||
        path.startsWith('/members/tambah') ||
        path.startsWith('/members/new') ||
        path.startsWith('/members/baru') ||
        path.startsWith('/members/kelulusan') ||
        path.startsWith('/members/roles')
      ) {
        return 'all';
      }

      if (path.endsWith('/pengurusan') || path.includes('/peranan/pengurusan')) {
        return 'pengurusan';
      }
      if (path.endsWith('/ahli') || path.includes('/peranan/ahli')) {
        return 'ahli';
      }
      if (path.endsWith('/waris') || path.includes('/peranan/waris')) {
        return 'waris';
      }
      if (path.endsWith('/semua') || path.endsWith('/all')) {
        return 'all';
      }
    }
    return 'all';
  };

  const getSortOptionFromUrl = (): DirectorySortOption => {
    if (typeof window !== 'undefined') {
      const params = new URLSearchParams(window.location.search);
      const qSort = (params.get('susunan') || params.get('sort') || '').toLowerCase().trim();
      const validOptions: DirectorySortOption[] = [
        'default',
        'id_asc',
        'id_desc',
        'age_desc',
        'age_asc',
        'bengkung_desc',
        'bengkung_asc',
        'name',
        'name_desc',
        'oldest',
        'newest'
      ];
      if (qSort === 'id') return 'id_asc';
      if (qSort === 'umur' || qSort === 'age') return 'age_desc';
      if (qSort === 'bengkung') return 'bengkung_desc';
      if (validOptions.includes(qSort as DirectorySortOption)) {
        return qSort as DirectorySortOption;
      }
    }
    return 'default';
  };

  const [roleFilter, setRoleFilter] = useState<string>(getRoleFilterFromUrl);
  const [directorySortBy, setDirectorySortBy] = useState<DirectorySortOption>(getSortOptionFromUrl);

  const handleSortChange = (newSort: DirectorySortOption) => {
    setDirectorySortBy(newSort);
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (newSort === 'default') {
        url.searchParams.delete('susunan');
        url.searchParams.delete('sort');
      } else {
        url.searchParams.set('susunan', newSort);
      }
      window.history.replaceState(null, '', url.pathname + (url.search ? url.search : '') + url.hash);
    }
  };

  const [isSyncingTurso, setIsSyncingTurso] = useState(false);

  // Helper functions for URL-based sub-tab and facet state
  const getPendingStatusFromUrl = (): 'menunggu' | 'diluluskan' | 'ditolak' | 'log' => {
    if (typeof window !== 'undefined') {
      const path = window.location.pathname.toLowerCase();
      if (path.includes('/diluluskan') || path.includes('/approved')) {
        return 'diluluskan';
      }
      if (path.includes('/ditolak') || path.includes('/rejected')) {
        return 'ditolak';
      }
      if (path.includes('/log') || path.includes('/audit')) {
        return 'log';
      }
      if (path.includes('/menunggu') || path.includes('/pending')) {
        return 'menunggu';
      }
    }
    return 'menunggu';
  };

  const getInitialRegistrationType = (): 'pesilat' | 'waris' => {
    if (typeof window !== 'undefined') {
      const path = window.location.pathname.toLowerCase();
      const params = new URLSearchParams(window.location.search);
      const qType = (params.get('type') || params.get('peranan') || params.get('role') || '').toLowerCase();
      if (qType === 'waris' || path.includes('/waris')) {
        return 'waris';
      }
    }
    return 'pesilat';
  };

  const [registrationType, setRegistrationTypeState] = useState<'pesilat' | 'waris'>(getInitialRegistrationType);

  const getInitialSubTab = (): 'direktori' | 'baru' | 'daftar' => {
    if (typeof window !== 'undefined') {
      const path = window.location.pathname.toLowerCase();
      if (path.startsWith('/members/daftar') || path.startsWith('/members/tambah') || path.startsWith('/members/new')) {
        return 'daftar';
      }
      if (path.startsWith('/members/baru') || path.startsWith('/members/kelulusan')) {
        return 'baru';
      }
    }
    return 'direktori';
  };

  // Sub-tabs: 'direktori' | 'baru' | 'daftar'
  const [activeSubTab, setActiveSubTabState] = useState<'direktori' | 'baru' | 'daftar'>(getInitialSubTab);

  const handleRegistrationTypeChange = (type: 'pesilat' | 'waris') => {
    setRegistrationTypeState(type);
    if (type === 'waris') {
      setRole('Waris');
    } else if (role === 'Waris') {
      setRole('Ahli');
    }
    if (typeof window !== 'undefined') {
      const url = new URL(window.location.href);
      if (type === 'waris') {
        url.searchParams.set('type', 'waris');
      } else {
        url.searchParams.delete('type');
      }
      window.history.replaceState(null, '', url.pathname + (url.search ? url.search : '') + url.hash);
    }
  };

  // Ahli Baru Status Sub-Filter: 'menunggu' | 'diluluskan' | 'ditolak' | 'log'
  const [pendingStatusTab, setPendingStatusTab] = useState<'menunggu' | 'diluluskan' | 'ditolak' | 'log'>(getPendingStatusFromUrl);
  const [pendingSearchTerm, setPendingSearchTerm] = useState('');
  const [pendingGelanggangFilter, setPendingGelanggangFilter] = useState<string>('all');
  const [pendingSortBy, setPendingSortBy] = useState<'newest' | 'oldest' | 'name' | 'gelanggang'>('newest');
  const [selectedPendingIds, setSelectedPendingIds] = useState<string[]>([]);
  const [isBulkApproving, setIsBulkApproving] = useState(false);
  const [isBulkRejecting, setIsBulkRejecting] = useState(false);
  const [showBulkRejectModal, setShowBulkRejectModal] = useState(false);
  const [copiedField, setCopiedField] = useState<{ id: string; type: string } | null>(null);
  const [previewPendingMember, setPreviewPendingMember] = useState<UserProfile | null>(null);

  const handleRoleFilterChange = (newRole: string) => {
    setRoleFilter(newRole);
    setCurrentPage(1);
    const targetPath = newRole === 'all' ? '/members' : `/members/${newRole}`;
    if (window.location.pathname !== targetPath) {
      window.history.pushState(null, '', targetPath);
    }
  };

  const handlePendingStatusTabChange = (status: 'menunggu' | 'diluluskan' | 'ditolak' | 'log') => {
    setPendingStatusTab(status);
    setSelectedPendingIds([]);
    const targetPath = `/members/baru/${status}`;
    if (window.location.pathname !== targetPath) {
      window.history.pushState(null, '', targetPath);
    }
  };

  const handleSubTabChange = (tab: 'direktori' | 'baru' | 'daftar', targetRegType?: 'pesilat' | 'waris') => {
    if (activeSubTab === 'direktori' && tab !== 'direktori') {
      saveViewScroll('member_directory');
    }
    if (tab === 'daftar') {
      const resolvedType = targetRegType || (roleFilter === 'waris' ? 'waris' : registrationType);
      setRegistrationTypeState(resolvedType);
      if (resolvedType === 'waris') {
        setRole('Waris');
      } else if (role === 'Waris') {
        setRole('Ahli');
      }
    }
    setActiveSubTabState(tab);
    setCurrentPage(1);
    const targetPath = tab === 'baru'
      ? `/members/baru/${pendingStatusTab}`
      : tab === 'daftar'
        ? ((targetRegType === 'waris' || (!targetRegType && (registrationType === 'waris' || roleFilter === 'waris'))) ? '/members/daftar?type=waris' : '/members/daftar')
        : roleFilter === 'all' ? '/members' : `/members/${roleFilter}`;
    if (window.location.pathname !== targetPath) {
      window.history.pushState(null, '', targetPath);
    }
    if (tab === 'direktori') {
      restoreViewScroll('member_directory');
    } else {
      resetScrollToTop();
    }
  };

  useEffect(() => {
    const handlePopState = () => {
      const path = window.location.pathname.toLowerCase();
      if (path.startsWith('/members/daftar') || path.startsWith('/members/tambah') || path.startsWith('/members/new')) {
        setActiveSubTabState('daftar');
        const params = new URLSearchParams(window.location.search);
        const qType = (params.get('type') || params.get('peranan') || params.get('role') || '').toLowerCase();
        if (qType === 'waris' || path.includes('/waris')) {
          setRegistrationTypeState('waris');
          setRole('Waris');
        } else {
          setRegistrationTypeState('pesilat');
          if (role === 'Waris') setRole('Ahli');
        }
      } else if (path.startsWith('/members/baru') || path.startsWith('/members/kelulusan')) {
        setActiveSubTabState('baru');
        setPendingStatusTab(getPendingStatusFromUrl());
      } else if (path === '/members' || path.startsWith('/members')) {
        setActiveSubTabState('direktori');
        setRoleFilter(getRoleFilterFromUrl());
        setDirectorySortBy(getSortOptionFromUrl());
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Ensure /members/baru or /members/kelulusan is normalized to have the facet slug
  useEffect(() => {
    if (activeSubTab === 'baru') {
      const path = window.location.pathname.toLowerCase();
      if (
        path === '/members/baru' ||
        path === '/members/baru/' ||
        path === '/members/kelulusan' ||
        path === '/members/kelulusan/'
      ) {
        window.history.replaceState(null, '', `/members/baru/${pendingStatusTab}`);
      }
    }
  }, [activeSubTab, pendingStatusTab]);

  // View / Edit / Delete member modal state
  const [selectedMember, setSelectedMember] = useState<UserProfile | null>(null);
  const [editingMember, setEditingMember] = useState<UserProfile | null>(null);

  // Superadmin profile photo upload in "Profil Butiran Pesilat"
  const memberPhotoInputRef = React.useRef<HTMLInputElement | null>(null);
  const [isUploadingMemberPhoto, setIsUploadingMemberPhoto] = useState(false);

  const canSuperadminChangePhoto = Boolean(
    currentUser && (
      isSuperAdmin(currentUser) ||
      currentUser.role === 'Superadmin' ||
      currentUser.role === 'Super Admin' ||
      currentUser.id?.toLowerCase() === 'asmm-011' ||
      currentUser.id?.toLowerCase() === 'asmm-001' ||
      currentUser.role === 'Pengerusi'
    )
  );
  const canSuperadminEditIc = canSuperadminChangePhoto;

  const handleSuperadminMemberPhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !selectedMember) return;

    if (!file.type.startsWith('image/')) {
      showToast('error', 'Fail Tidak Sah', 'Sila pilih fail imej yang sah (PNG, JPG, JPEG, WEBP).');
      return;
    }

    e.target.value = '';
    setIsUploadingMemberPhoto(true);

    try {
      const uploadedDataUrl = await uploadMemberPhotoToAhliDriveJson(
        file,
        selectedMember.icNo || selectedMember.id,
        selectedMember.name
      );

      const updatedProfile: UserProfile = {
        ...selectedMember,
        photoUrl: uploadedDataUrl
      };

      await saveMemberDB(updatedProfile);
      setMembers(prev => prev.map(m => m.id === updatedProfile.id ? updatedProfile : m));
      setSelectedMember(updatedProfile);

      if (currentUser?.id === updatedProfile.id || currentUser?.icNo === updatedProfile.icNo) {
        setCurrentUser(updatedProfile);
      }

      window.dispatchEvent(new Event('asmm_db_updated'));
      showToast('success', 'Foto Profil Dikemaskini', `Foto profil ${selectedMember.name} berjaya dikemaskini.`);
    } catch (err) {
      console.error('Ralat kemaskini foto profil ahli:', err);
      showToast('error', 'Gagal Kemaskini', 'Ralat ketika memuat naik dan menyimpan foto ahli.');
    } finally {
      setIsUploadingMemberPhoto(false);
    }
  };

  const handleSuperadminResetMemberPhoto = async () => {
    if (!selectedMember) return;
    setIsUploadingMemberPhoto(true);
    try {
      const updatedProfile: UserProfile = {
        ...selectedMember,
        photoUrl: '/default-avatar.svg'
      };

      await saveMemberDB(updatedProfile);
      setMembers(prev => prev.map(m => m.id === updatedProfile.id ? updatedProfile : m));
      setSelectedMember(updatedProfile);

      if (currentUser?.id === updatedProfile.id || currentUser?.icNo === updatedProfile.icNo) {
        setCurrentUser(updatedProfile);
      }

      window.dispatchEvent(new Event('asmm_db_updated'));
      showToast('success', 'Foto Direset', `Foto profil ${selectedMember.name} dikembalikan kepada avatar lalai.`);
    } catch (err) {
      console.error('Ralat reset foto:', err);
      showToast('error', 'Ralat', 'Gagal mereset foto profil.');
    } finally {
      setIsUploadingMemberPhoto(false);
    }
  };

  // Form states for new member registration
  const [name, setName] = useState('');
  const [icNo, setIcNo] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState('');
  const [role, setRole] = useState<Role>('Ahli');
  const [gelanggang, setGelanggang] = useState<Gelanggang>('Pusaka Warisan');
  const [bengkung, setBengkung] = useState<BengkungLevel>('Putih (Pesilat Asas)');
  const [isOldMemberManual, setIsOldMemberManual] = useState<boolean>(true);

  // Family Attachment States for New Member
  const [isFamilyAttached, setIsFamilyAttached] = useState<boolean>(false);
  const [selectedParentMemberId, setSelectedParentMemberId] = useState<string>('');
  const [familyRelationship, setFamilyRelationship] = useState<string>('Anak');
  const [parentSearchTerm, setParentSearchTerm] = useState<string>('');

  const newMemberIcDetails = useMemo(() => {
    const clean = sanitizeIc(icNo);
    if (!clean || clean.length < 6) return null;
    return getIcDetails(clean);
  }, [icNo]);

  const eligibleFamilyHeads = useMemo(() => {
    return members.filter(m => m.status !== 'Ditolak');
  }, [members]);

  const filteredFamilyHeads = useMemo(() => {
    const trimmed = parentSearchTerm.trim();
    if (!trimmed) {
      return eligibleFamilyHeads.slice(0, 15);
    }
    return eligibleFamilyHeads
      .map(m => {
        const { score, isMatch } = scoreMemberFuzzy(trimmed, m);
        return { member: m, score, isMatch };
      })
      .filter(item => item.isMatch)
      .sort((a, b) => b.score - a.score)
      .map(item => item.member)
      .slice(0, 30);
  }, [eligibleFamilyHeads, parentSearchTerm]);

  const selectedParentMember = useMemo(() => {
    if (!selectedParentMemberId) return null;
    return members.find(m => m.id === selectedParentMemberId) || null;
  }, [members, selectedParentMemberId]);

  // Waris Child Linkage States for New Waris Registration
  const [isWarisLinkPesilat, setIsWarisLinkPesilat] = useState<boolean>(false);
  const [selectedWarisChildMemberId, setSelectedWarisChildMemberId] = useState<string>('');
  const [warisChildRelationship, setWarisChildRelationship] = useState<string>('Anak');
  const [warisChildSearchTerm, setWarisChildSearchTerm] = useState<string>('');

  const eligibleWarisChildren = useMemo(() => {
    return members.filter(m => m.status !== 'Ditolak' && m.role !== 'Waris');
  }, [members]);

  const filteredWarisChildren = useMemo(() => {
    const trimmed = warisChildSearchTerm.trim();
    if (!trimmed) {
      return eligibleWarisChildren.slice(0, 15);
    }
    return eligibleWarisChildren
      .map(m => {
        const { score, isMatch } = scoreMemberFuzzy(trimmed, m);
        return { member: m, score, isMatch };
      })
      .filter(item => item.isMatch)
      .sort((a, b) => b.score - a.score)
      .map(item => item.member)
      .slice(0, 30);
  }, [eligibleWarisChildren, warisChildSearchTerm]);

  const selectedWarisChildMember = useMemo(() => {
    if (!selectedWarisChildMemberId) return null;
    return members.find(m => m.id === selectedWarisChildMemberId) || null;
  }, [members, selectedWarisChildMemberId]);

  useEffect(() => {
    getMembersDB().then(setMembers);
    getBengkungRanksDB().then(setBengkungRanksList).catch(() => { });

    const handleDbUpdate = () => {
      getMembersDB().then(setMembers).catch(() => { });
      getBengkungRanksDB().then(setBengkungRanksList).catch(() => { });
    };
    window.addEventListener('asmm_db_updated', handleDbUpdate);
    window.addEventListener('storage', handleDbUpdate);
    return () => {
      window.removeEventListener('asmm_db_updated', handleDbUpdate);
      window.removeEventListener('storage', handleDbUpdate);
    };
  }, []);

  const handleSyncTurso = async () => {
    setIsSyncingTurso(true);
    showToast('info', 'Memuat naik data ke Pangkalan Data Turso...');
    try {
      await syncAllMembersToTursoDB(members);
      const updated = await getMembersDB();
      setMembers(updated);
      showToast('success', 'Pangkalan Data Turso Berjaya Disegerakkan!');
    } catch {
      showToast('error', 'Gagal menyegerakkan dengan Turso DB');
    } finally {
      setIsSyncingTurso(false);
    }
  };

  const [rejectingMemberId, setRejectingMemberId] = useState<string | null>(null);

  const showApprovalPanel = currentUser && isManagementOrTrainer(currentUser);

  // 1. Pending Members (Menunggu Pengesahan)
  const pendingMembers = useMemo(() => {
    return members.filter(m => m.status === 'Menunggu Pengesahan');
  }, [members]);

  // 2. Approved Members (Telah Diluluskan oleh pihak pengurusan / aktif)
  const approvedMembers = useMemo(() => {
    return members.filter(m => m.status === 'Aktif' && (m.approvedBy || m.approvedByName));
  }, [members]);

  // 3. Rejected Members (Permohonan Ditolak - disimpan dalam rekod dan tidak dipadam)
  const rejectedMembers = useMemo(() => {
    return members.filter(m => m.status === 'Ditolak');
  }, [members]);

  // 4. Audit Log Items (Semua sejarah permohonan, kelulusan, dan penolakan)
  const auditLogItems = useMemo(() => {
    return members
      .filter(m => m.approvedBy || m.approvedByName || m.rejectedBy || m.rejectedByName || m.status === 'Menunggu Pengesahan' || m.status === 'Ditolak')
      .sort((a, b) => {
        const timeA = a.approvedAt || a.rejectedAt || a.joinedDate || '';
        const timeB = b.approvedAt || b.rejectedAt || b.joinedDate || '';
        return timeB.localeCompare(timeA);
      });
  }, [members]);

  const isLeader = currentUser?.role === 'Ketua Gelanggang';

  const myPending = useMemo(() => {
    if (!currentUser) return [];
    if (isLeader && currentUser.gelanggang && pendingGelanggangFilter === 'all') {
      return pendingMembers.filter(m => !m.gelanggang || m.gelanggang === 'Semua Gelanggang' || m.gelanggang === 'ASMM' || m.gelanggang.toLowerCase().includes(currentUser.gelanggang.toLowerCase()) || currentUser.gelanggang.toLowerCase().includes(m.gelanggang.toLowerCase()));
    }
    return pendingMembers;
  }, [pendingMembers, currentUser, isLeader, pendingGelanggangFilter]);

  const myApproved = useMemo(() => {
    if (!currentUser) return [];
    if (isLeader && currentUser.gelanggang && pendingGelanggangFilter === 'all') {
      return approvedMembers.filter(m => !m.gelanggang || m.gelanggang === 'Semua Gelanggang' || m.gelanggang === 'ASMM' || m.gelanggang.toLowerCase().includes(currentUser.gelanggang.toLowerCase()) || currentUser.gelanggang.toLowerCase().includes(m.gelanggang.toLowerCase()));
    }
    return approvedMembers;
  }, [approvedMembers, currentUser, isLeader, pendingGelanggangFilter]);

  const myRejected = useMemo(() => {
    if (!currentUser) return [];
    if (isLeader && currentUser.gelanggang && pendingGelanggangFilter === 'all') {
      return rejectedMembers.filter(m => !m.gelanggang || m.gelanggang === 'Semua Gelanggang' || m.gelanggang === 'ASMM' || m.gelanggang.toLowerCase().includes(currentUser.gelanggang.toLowerCase()) || currentUser.gelanggang.toLowerCase().includes(m.gelanggang.toLowerCase()));
    }
    return rejectedMembers;
  }, [rejectedMembers, currentUser, isLeader, pendingGelanggangFilter]);

  const currentTabItems = useMemo(() => {
    if (pendingStatusTab === 'menunggu') return myPending;
    if (pendingStatusTab === 'diluluskan') return myApproved;
    if (pendingStatusTab === 'ditolak') return myRejected;
    return auditLogItems;
  }, [pendingStatusTab, myPending, myApproved, myRejected, auditLogItems]);

  const filteredPending = useMemo(() => {
    return currentTabItems.filter(m => {
      const matchSearch = pendingSearchTerm === '' ||
        m.name.toLowerCase().includes(pendingSearchTerm.toLowerCase()) ||
        (m.icNo && m.icNo.includes(pendingSearchTerm)) ||
        (m.phone && m.phone.includes(pendingSearchTerm)) ||
        (m.email && m.email.toLowerCase().includes(pendingSearchTerm.toLowerCase())) ||
        (m.kinName && m.kinName.toLowerCase().includes(pendingSearchTerm.toLowerCase())) ||
        (m.kinPhone && m.kinPhone.includes(pendingSearchTerm)) ||
        (m.approvedByName && m.approvedByName.toLowerCase().includes(pendingSearchTerm.toLowerCase())) ||
        (m.rejectedByName && m.rejectedByName.toLowerCase().includes(pendingSearchTerm.toLowerCase())) ||
        (m.address && m.address.toLowerCase().includes(pendingSearchTerm.toLowerCase()));
      const matchGelanggang = pendingGelanggangFilter === 'all' || m.gelanggang === pendingGelanggangFilter;
      return matchSearch && matchGelanggang;
    });
  }, [currentTabItems, pendingSearchTerm, pendingGelanggangFilter]);

  const pendingCountsByGelanggang = useMemo(() => {
    const counts: Record<string, number> = {
      all: currentTabItems.length,
      'Pusaka Warisan': 0,
      'Wadi AnNour': 0,
      'Paya Besar': 0,
    };
    currentTabItems.forEach(m => {
      if (m.gelanggang && counts[m.gelanggang] !== undefined) {
        counts[m.gelanggang]++;
      }
    });
    return counts;
  }, [currentTabItems]);

  const sortedFilteredPending = useMemo(() => {
    return [...filteredPending].sort((a, b) => {
      if (pendingSortBy === 'newest') {
        const dateA = a.approvedAt || a.rejectedAt || a.joinedDate || '';
        const dateB = b.approvedAt || b.rejectedAt || b.joinedDate || '';
        return dateB.localeCompare(dateA);
      } else if (pendingSortBy === 'oldest') {
        const dateA = a.approvedAt || a.rejectedAt || a.joinedDate || '';
        const dateB = b.approvedAt || b.rejectedAt || b.joinedDate || '';
        return dateA.localeCompare(dateB);
      } else if (pendingSortBy === 'name') {
        return a.name.localeCompare(b.name, 'ms');
      } else if (pendingSortBy === 'gelanggang') {
        return (a.gelanggang || '').localeCompare(b.gelanggang || '');
      }
      return 0;
    });
  }, [filteredPending, pendingSortBy]);


  const formatSubmissionDate = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleDateString('ms-MY', {
        day: 'numeric',
        month: 'short',
        year: 'numeric'
      });
    } catch {
      return dateStr;
    }
  };

  const formatDateTime = (dateStr?: string) => {
    if (!dateStr) return '-';
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;
      return d.toLocaleString('ms-MY', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateStr;
    }
  };

  const handleCopyText = (id: string, type: string, text: string) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedField({ id, type });
    showToast('info', 'Disalin', text);
    setTimeout(() => {
      setCopiedField((prev) => (prev?.id === id && prev?.type === type ? null : prev));
    }, 2000);
  };

  const handleSelectAllPending = () => {
    if (selectedPendingIds.length === filteredPending.length) {
      setSelectedPendingIds([]);
    } else {
      setSelectedPendingIds(filteredPending.map(m => m.id));
    }
  };

  const handleToggleSelectPending = (id: string) => {
    setSelectedPendingIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const handleBulkApprove = async () => {
    if (selectedPendingIds.length === 0) return;
    setIsBulkApproving(true);
    showToast('info', 'Meluluskan Ahli Terpilih...', `Memproses ${selectedPendingIds.length} permohonan.`);
    try {
      const reviewerId = currentUser?.id || 'pengurusan';
      const reviewerName = currentUser?.name || 'Pihak Pengurusan';
      const now = new Date().toISOString();
      const toApprove = members.filter(m => selectedPendingIds.includes(m.id));
      for (const m of toApprove) {
        await saveMemberDB({
          ...m,
          status: 'Aktif',
          approvedBy: reviewerId,
          approvedByName: reviewerName,
          approvedAt: now,
          rejectedBy: undefined,
          rejectedByName: undefined,
          rejectedAt: undefined,
          rejectionReason: undefined
        });
      }
      await refreshMembers();
      setSelectedPendingIds([]);
      showToast('success', 'Kelulusan Berkelompok Selesai!', `${toApprove.length} ahli telah berjaya diluluskan dan diaktifkan.`);
    } catch {
      showToast('error', 'Ralat Kelulusan Berkelompok', 'Gagal memproses permohonan terpilih.');
    } finally {
      setIsBulkApproving(false);
    }
  };

  const handleBulkReject = async () => {
    if (selectedPendingIds.length === 0) return;
    setIsBulkRejecting(true);
    try {
      const reviewerId = currentUser?.id || 'pengurusan';
      const reviewerName = currentUser?.name || 'Pihak Pengurusan';
      const now = new Date().toISOString();
      for (const id of selectedPendingIds) {
        const target = members.find(m => m.id === id);
        if (target) {
          await saveMemberDB({
            ...target,
            status: 'Ditolak',
            rejectedBy: reviewerId,
            rejectedByName: reviewerName,
            rejectedAt: now
          });
        }
      }
      await refreshMembers();
      setSelectedPendingIds([]);
      setShowBulkRejectModal(false);
      showToast('info', 'Permohonan Ditolak', `${selectedPendingIds.length} permohonan telah ditolak dan disimpan dalam rekod audit.`);
    } catch {
      showToast('error', 'Ralat Sistem', 'Gagal memproses penolakan terpilih.');
    } finally {
      setIsBulkRejecting(false);
    }
  };

  const refreshMembers = async (forceLiveAjax: boolean = false) => {
    const fresh = forceLiveAjax ? await refreshMembersLiveDB() : await getMembersDB();
    setMembers(fresh);
    window.dispatchEvent(new Event('asmm_db_updated'));
    return fresh;
  };

  const handleApprove = async (m: UserProfile) => {
    showToast('info', 'Mengemaskini status...', 'Sila tunggu sebentar.');
    const reviewerId = currentUser?.id || 'pengurusan';
    const reviewerName = currentUser?.name || 'Pihak Pengurusan';
    const now = new Date().toISOString();
    const updatedMember: UserProfile = {
      ...m,
      status: 'Aktif',
      approvedBy: reviewerId,
      approvedByName: reviewerName,
      approvedAt: now,
      rejectedBy: undefined,
      rejectedByName: undefined,
      rejectedAt: undefined,
      rejectionReason: undefined
    };
    try {
      await saveMemberDB(updatedMember);
      await refreshMembers();
      if (previewPendingMember?.id === m.id) {
        setPreviewPendingMember(null);
      }
      showToast('success', 'Keahlian Diluluskan!', `Akaun ${m.name} kini aktif dan direkodkan di bawah kelulusan ${reviewerName}.`);
    } catch {
      showToast('error', 'Ralat Kelulusan', 'Gagal meluluskan keahlian. Sila cuba lagi.');
    }
  };

  const handleReject = async (id: string, name: string) => {
    try {
      const target = members.find(m => m.id === id);
      if (!target) return;
      const reviewerId = currentUser?.id || 'pengurusan';
      const reviewerName = currentUser?.name || 'Pihak Pengurusan';
      const now = new Date().toISOString();
      const updatedMember: UserProfile = {
        ...target,
        status: 'Ditolak',
        rejectedBy: reviewerId,
        rejectedByName: reviewerName,
        rejectedAt: now
      };
      await saveMemberDB(updatedMember);
      await refreshMembers();
      if (previewPendingMember?.id === id) {
        setPreviewPendingMember(null);
      }
      showToast('info', 'Permohonan Ditolak', `Permohonan pesilat ${name} telah ditolak dan disimpan dalam rekod audit.`);
      setRejectingMemberId(null);
    } catch {
      showToast('error', 'Ralat Sistem', 'Gagal memproses penolakan permohonan. Sila cuba lagi.');
    }
  };

  const handleAddMember = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!isExecutive) {
      showToast('error', 'Akses Disekat', 'Hanya pihak Pengurusan dan Superadmin dibenarkan mendaftar ahli atau waris.');
      return;
    }

    const cleanIc = sanitizeIc(icNo);
    const cleanPhone = sanitizePhone(phone);
    const cleanEmail = email.trim().toLowerCase();

    // ==========================================
    // 1. PENDAFTARAN WARIS BAHARU (PANTAS: NAMA, IC & EMEL SAHAJA)
    // ==========================================
    if (registrationType === 'waris' || role === 'Waris') {
      if (!name.trim()) {
        showToast('error', 'Nama Diperlukan', 'Sila masukkan nama penuh waris.');
        return;
      }

      if (!cleanIc) {
        showToast('error', 'No. IC Diperlukan', 'Sila masukkan No. Kad Pengenalan (IC) waris.');
        return;
      }

      if (!cleanEmail) {
        showToast('error', 'Emel Diperlukan', 'Sila masukkan alamat emel rasmi waris.');
        return;
      }

      if (!isValidEmail(cleanEmail)) {
        showToast('error', 'Format Emel Tidak Sah', 'Sila masukkan alamat emel yang sah (contoh: waris@gmail.com).');
        return;
      }

      const currentMembers = await getMembersDB();

      // Semak No. IC bertindih
      const duplicateIc = findDuplicateIcMember(cleanIc, undefined, currentMembers);
      if (duplicateIc) {
        showToast('error', 'No. IC Telah Digunakan', `No. IC ini sudah didaftarkan kepada ${duplicateIc.name} (${duplicateIc.id}).`);
        return;
      }

      // Semak Emel bertindih
      const duplicateEmail = currentMembers.find(m => m.email && m.email.toLowerCase().trim() === cleanEmail);
      if (duplicateEmail) {
        showToast('error', 'Emel Telah Digunakan', `Emel ${cleanEmail} sudah didaftarkan kepada ${duplicateEmail.name} (${duplicateEmail.id}).`);
        return;
      }

      // Semak No. Telefon bertindih jika diisi
      if (cleanPhone) {
        const duplicatePhone = findDuplicatePhoneMember(cleanPhone, undefined, currentMembers);
        if (duplicatePhone) {
          showToast('error', 'No. Telefon Telah Digunakan', `No. Telefon ${cleanPhone} sudah digunakan oleh ahli lain (${duplicatePhone.name} - ${duplicatePhone.id}).`);
          return;
        }
      }

      const nextId = await getNextMemberId();
      const resolvedGelanggang = (gelanggang && gelanggang !== 'Semua Gelanggang') ? gelanggang : 'Pusaka Warisan';

      const newWaris: UserProfile = {
        id: nextId,
        name: toTitleCase(name.trim()),
        icNo: cleanIc,
        email: cleanEmail,
        phone: cleanPhone || '',
        address: address.trim() || '',
        role: 'Waris',
        roles: ['Waris'],
        gelanggang: resolvedGelanggang,
        bengkung: '' as any,
        bengkungBadge: '',
        photoUrl: '/default-avatar.svg',
        joinedDate: new Date().toISOString().split('T')[0],
        status: 'Aktif',
        isOldMember: false,
        onboardingStatus: 'completed',
        isAlsoPesilat: false,
        password: cleanIc
      };

      await saveMemberDB(newWaris);

      // Pautkan pesilat sedia ada jika dipilih secara pilihan
      let linkedPesilatName = '';
      if (isWarisLinkPesilat && selectedWarisChildMemberId) {
        const targetChild = currentMembers.find(m => m.id === selectedWarisChildMemberId);
        if (targetChild) {
          const updatedChild: UserProfile = {
            ...targetChild,
            parentId: newWaris.id,
            kinName: newWaris.name,
            kinPhone: newWaris.phone || targetChild.kinPhone,
            relationship: warisChildRelationship || 'Anak'
          };
          await saveMemberDB(updatedChild);
          linkedPesilatName = targetChild.name;
        }
      }

      await refreshMembers();
      window.dispatchEvent(new Event('asmm_db_updated'));

      showToast(
        'success',
        'Pendaftaran Waris Berjaya',
        linkedPesilatName
          ? `Waris ${newWaris.name} (${newWaris.id}) berjaya didaftarkan dan dipautkan dengan pesilat ${linkedPesilatName}.`
          : `Waris ${newWaris.name} (${newWaris.id}) berjaya didaftarkan dengan emel ${cleanEmail} & No. IC ${cleanIc}.`
      );

      // Reset form
      setName('');
      setIcNo('');
      setEmail('');
      setPhone('');
      setAddress('');
      setIsWarisLinkPesilat(false);
      setSelectedWarisChildMemberId('');
      setWarisChildRelationship('Anak');
      setWarisChildSearchTerm('');

      // Pergi ke direktori paparan Waris supaya admin boleh terus melihat rekod baharu
      setRoleFilter('waris');
      handleSubTabChange('direktori');
      return;
    }

    // ==========================================
    // 2. PENDAFTARAN PESILAT BAHARU (ALIRAN SEDIA ADA)
    // ==========================================
    if (!name.trim() || !cleanIc) {
      showToast('error', 'Maklumat Tidak Lengkap', 'Sila isi nama dan No. IC pesilat.');
      return;
    }

    if (isFamilyAttached && !selectedParentMemberId) {
      showToast('error', 'Pilih Ahli Keluarga', 'Sila pilih ahli keluarga / waris untuk dipautkan, atau nyahpilih opsyen pautan keluarga.');
      return;
    }

    if (cleanEmail && !isValidEmail(cleanEmail)) {
      showToast('error', 'Format Emel Tidak Sah', 'Sila masukkan alamat emel yang sah (contoh: pesilat@domain.com).');
      return;
    }

    const currentMembers = await getMembersDB();

    // Check duplicate IC
    const duplicateIc = findDuplicateIcMember(cleanIc, undefined, currentMembers);
    if (duplicateIc) {
      showToast('error', 'No. IC Telah Wujud', `No. IC ini sudah didaftarkan (${duplicateIc.name} - ${duplicateIc.id}).`);
      return;
    }

    const selectedParent = isFamilyAttached && selectedParentMemberId
      ? currentMembers.find(m => m.id === selectedParentMemberId)
      : null;

    // Check duplicate Phone (allow sharing parent's/guardian's phone number in family attachment)
    if (cleanPhone) {
      const duplicatePhone = findDuplicatePhoneMember(cleanPhone, undefined, currentMembers);
      const isParentPhone = selectedParent && (
        duplicatePhone?.id === selectedParent.id ||
        (selectedParent.phone && sanitizePhone(selectedParent.phone) === cleanPhone)
      );
      if (duplicatePhone && !isParentPhone) {
        showToast('error', 'No. Telefon Telah Digunakan', `No. Telefon ${cleanPhone} sudah digunakan oleh ahli lain (${duplicatePhone.name} - ${duplicatePhone.id}).`);
        return;
      }
    }

    // Check duplicate email
    if (cleanEmail) {
      const duplicateEmail = currentMembers.find(m => m.email && m.email.toLowerCase().trim() === cleanEmail);
      if (duplicateEmail) {
        showToast('error', 'Emel Telah Digunakan', 'Emel ini sudah berdaftar.');
        return;
      }
    }

    const nextId = await getNextMemberId();
    const isOld = role === 'Ahli' ? isOldMemberManual : true;

    const newMember: UserProfile = {
      id: nextId,
      name: toTitleCase(name.trim()),
      icNo: cleanIc,
      email: cleanEmail || '',
      phone: cleanPhone || (selectedParent ? selectedParent.phone || '' : ''),
      address: address.trim() || (selectedParent ? selectedParent.address || '' : ''),
      role,
      roles: [role],
      gelanggang: gelanggang === 'Semua Gelanggang' ? (selectedParent?.gelanggang || 'Pusaka Warisan') : gelanggang,
      bengkung,
      bengkungBadge: '/badges/putih1-pesilat-muda.png',
      photoUrl: '/default-avatar.svg',
      joinedDate: new Date().toISOString().split('T')[0],
      status: 'Aktif',
      isOldMember: isOld,
      onboardingStatus: isOld ? 'completed' : 'pending_payment',
      parentId: selectedParent ? selectedParent.id : undefined,
      relationship: selectedParent ? familyRelationship : undefined,
      kinName: selectedParent ? selectedParent.name : undefined,
      kinPhone: selectedParent ? selectedParent.phone : undefined
    };

    if (role === 'Ahli' && !isOld) {
      const onbInv = createOnboardingInvoice(newMember);
      await saveInvoiceDB(onbInv);
      newMember.onboardingInvoiceId = onbInv.id;
    }

    await saveMemberDB(newMember);
    await refreshMembers();
    window.dispatchEvent(new Event('asmm_db_updated'));

    showToast(
      'success',
      'Pendaftaran Berjaya',
      selectedParent
        ? `Ahli ${name.trim()} berjaya didaftarkan dan dipautkan sebagai ${familyRelationship} kepada ${selectedParent.name}.`
        : `Ahli ${name.trim()} (${isOld ? 'Ahli Lama' : 'Ahli Baharu'}) berjaya didaftarkan.`
    );

    // Reset form
    setName('');
    setIcNo('');
    setEmail('');
    setPhone('');
    setAddress('');
    setIsFamilyAttached(false);
    setSelectedParentMemberId('');
    setFamilyRelationship('Anak');
    setParentSearchTerm('');

    // Navigate back to directory
    handleSubTabChange('direktori');
  };

  const handleUpdateMember = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!editingMember) return;

    const cleanIc = sanitizeIc(editingMember.icNo);
    const cleanPhone = sanitizePhone(editingMember.phone);
    const cleanEmail = (editingMember.email || '').trim().toLowerCase();

    if (cleanEmail && !isValidEmail(cleanEmail)) {
      showToast('error', 'Format Emel Tidak Sah', 'Sila semak semula alamat emel.');
      return;
    }

    const currentMembers = await getMembersDB();

    // Check duplicate IC on another member
    if (cleanIc) {
      const duplicateIc = findDuplicateIcMember(cleanIc, editingMember.id, currentMembers);
      if (duplicateIc) {
        showToast('error', 'No. IC Telah Digunakan', `No. IC ini sudah didaftarkan kepada ahli lain (${duplicateIc.name} - ${duplicateIc.id}).`);
        return;
      }
    }

    // Check duplicate Phone on another member
    if (cleanPhone) {
      const duplicatePhone = findDuplicatePhoneMember(cleanPhone, editingMember.id, currentMembers);
      if (duplicatePhone) {
        showToast('error', 'No. Telefon Telah Digunakan', `No. telefon ini sudah digunakan oleh ahli lain (${duplicatePhone.name} - ${duplicatePhone.id}).`);
        return;
      }
    }

    // Check duplicate email on another member
    if (cleanEmail) {
      const duplicateEmail = currentMembers.find(m => (m.id || '').trim().toLowerCase() !== (editingMember.id || '').trim().toLowerCase() && m.email && m.email.toLowerCase().trim() === cleanEmail);
      if (duplicateEmail) {
        showToast('error', 'Emel Telah Digunakan', 'Emel ini sudah digunakan ahli lain.');
        return;
      }
    }

    const updatedMember: UserProfile = {
      ...editingMember,
      name: toTitleCase(editingMember.name.trim()),
      icNo: cleanIc,
      phone: cleanPhone,
      email: cleanEmail,
      address: (editingMember.address || '').trim(),
      gelanggang: editingMember.gelanggang === 'Semua Gelanggang' ? 'ASMM' : editingMember.gelanggang
    };

    await saveMemberDB(updatedMember);
    await refreshMembers();

    // If updated member is currently logged-in user, immediately sync AuthContext
    if (
      currentUser &&
      (currentUser.id === updatedMember.id ||
        (cleanIc && sanitizeIc(currentUser.icNo) === cleanIc))
    ) {
      setCurrentUser({ ...currentUser, ...updatedMember });
    }

    showToast('success', 'Rekod Dikemaskini', `Maklumat ${updatedMember.name} disimpan.`);
    setEditingMember(null);
  };

  const [confirmDeleteMember, setConfirmDeleteMember] = useState<UserProfile | null>(null);
  const [deleteWholeFamily, setDeleteWholeFamily] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  const deleteTargetFamilyMembers = useMemo(() => {
    if (!confirmDeleteMember) return [];
    return members.filter(m => isChildOfParent(m, confirmDeleteMember) && m.id?.toLowerCase() !== confirmDeleteMember.id?.toLowerCase());
  }, [confirmDeleteMember, members]);

  const handleDeleteMember = async (id: string, memberName?: string) => {
    const m = members.find(mem => mem.id?.toLowerCase() === id.toLowerCase()) ||
      (previewPendingMember?.id?.toLowerCase() === id.toLowerCase() ? previewPendingMember : null) ||
      (selectedMember?.id?.toLowerCase() === id.toLowerCase() ? selectedMember : null) ||
      ({ id, name: memberName || id, role: 'Ahli' } as UserProfile);
    setConfirmDeleteMember(m);
    setDeleteWholeFamily(false);
  };

  const confirmDelete = async () => {
    if (!confirmDeleteMember || isDeleting) return;
    const targetMember = confirmDeleteMember;
    const targetId = targetMember.id;
    const targetName = targetMember.name;
    const familyIds = deleteTargetFamilyMembers.map(f => f.id);
    const willDeleteFamily = deleteWholeFamily && familyIds.length > 0;
    const allIdsToDelete = willDeleteFamily ? [targetId, ...familyIds] : [targetId];

    setIsDeleting(true);

    // Save exact scroll coordinates so page position doesn't change
    const scrollY = window.scrollY || window.pageYOffset || document.documentElement.scrollTop;
    const scrollX = window.scrollX || window.pageXOffset || document.documentElement.scrollLeft;

    // 1. Optimistic instant removal from React state
    setMembers(prev => prev.filter(m => !allIdsToDelete.some(tid => tid.toLowerCase() === (m.id || '').toLowerCase())));
    setSelectedMember(null);
    setConfirmDeleteMember(null);

    try {
      // 2. Perform AJAX deletion & immediate local state update
      const freshMembers = await deleteMemberDB(targetId, {
        deleteFamily: willDeleteFamily,
        familyMemberIds: familyIds
      });
      setMembers(freshMembers);
    } catch (err) {
      console.error('Delete member error:', err);
      const fresh = await refreshMembersLiveDB();
      setMembers(fresh);
    } finally {
      setIsDeleting(false);
      // Ensure page scroll position is retained perfectly without jumping
      requestAnimationFrame(() => {
        window.scrollTo({ top: scrollY, left: scrollX, behavior: 'instant' as ScrollBehavior });
      });
    }
  };

  const isManagement = currentUser && isManagementOrTrainer(currentUser);

  const roleFilters = useMemo(() => {
    const filters = [
      { id: 'all', label: 'Semua' },
      { id: 'pengurusan', label: 'Pengurusan' },
      { id: 'ahli', label: 'Ahli' },
    ];
    if (isManagement) {
      filters.push({ id: 'waris', label: 'Waris' });
    }
    return filters;
  }, [isManagement]);

  const renderIcNo = (m: UserProfile) => {
    if (!canViewMemberSensitiveInfo(currentUser, m)) {
      return null;
    }
    return <span>{m.icNo}</span>;
  };

  const filteredMembers = useMemo(() => {
    return members.filter((m) => {
      if (m.status === 'Menunggu Pengesahan' || m.status === 'Ditolak') return false;

      const canViewSensitive = canViewMemberSensitiveInfo(currentUser, m);
      const matchesSearch =
        m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (canViewSensitive && m.icNo && m.icNo.includes(searchTerm)) ||
        (canViewSensitive && m.phone && m.phone.includes(searchTerm)) ||
        (canViewSensitive && m.email && m.email.toLowerCase().includes(searchTerm.toLowerCase()));
      // "if i select pssasmm here, mean show all not asmm only, asmm means all gelanggang"
      const isAllGelanggang = (g?: string | null): boolean => {
        if (!g) return true;
        const clean = g.trim().toLowerCase();
        return clean === 'pssasmm' || clean === 'asmm' || clean === 'semua gelanggang' || clean === 'semua' || clean === 'all';
      };

      const matchesGelanggang =
        isAllGelanggang(selectedGelanggang) ||
        m.gelanggang === selectedGelanggang ||
        isAllGelanggang(m.gelanggang);

      const matchesBengkung = bengkungFilter === 'all' || m.bengkung.toLowerCase().includes(bengkungFilter.toLowerCase());
      const mRoles: Role[] = (m.roles && m.roles.length > 0) ? m.roles : [m.role || 'Ahli'];
      let matchesRole = true;
      if (roleFilter === 'all') {
        matchesRole = mRoles.some(r => r !== 'Waris');
      } else if (roleFilter === 'pengurusan') {
        matchesRole = mRoles.some(r => isPengurusanRole(r));
      } else if (roleFilter === 'ahli') {
        matchesRole = mRoles.includes('Ahli') || mRoles.includes('Ahli Seumur Hidup');
      } else if (roleFilter === 'waris') {
        matchesRole = mRoles.includes('Waris');
      } else {
        matchesRole = mRoles.includes(roleFilter as Role);
      }

      return matchesSearch && matchesGelanggang && matchesBengkung && matchesRole;
    });
  }, [members, searchTerm, selectedGelanggang, bengkungFilter, roleFilter]);

  const [currentPage, setCurrentPage] = useState<number>(1);
  const itemsPerPage = 12;

  // Reset page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedGelanggang, bengkungFilter, roleFilter, directorySortBy]);

  const getRoleSortWeight = (role?: string | null): number => {
    if (!role) return 99;
    const r = role.toLowerCase().trim();
    if (r === 'pengerusi') return 1;
    if (r === 'timbalan pengerusi') return 2;
    if (r === 'setiausaha') return 3;
    if (r === 'pen. setiausaha' || r === 'penolong setiausaha') return 4;
    if (r === 'bendahari') return 5;
    if (r === 'ketua gelanggang') return 6;
    if (r === 'bendahari gelanggang' || r.includes('bendahari gelanggang')) return 7;
    if (r === 'ketua biro latihan & dakwah') return 8;
    if (r === 'ketua biro sosial & kebajikan') return 9;
    if (r === 'ketua biro keusahawanan & sukan') return 10;
    if (r === 'ketua biro media & penerangan') return 11;
    if (r.startsWith('ketua biro') || r.includes('biro')) return 12;
    if (r === 'ajk' || r === 'ahli jawatankuasa') return 13;
    if (r === 'ahli seumur hidup' || r.includes('seumur hidup')) return 16;
    if (r === 'ahli' || r === 'ahli biasa') return 20;
    if (r === 'waris') return 30;
    return 50;
  };

  const getMemberSortWeight = (m: UserProfile): number => {
    const weights: number[] = [getRoleSortWeight(m.role)];
    if (m.roles && Array.isArray(m.roles)) {
      m.roles.forEach(r => weights.push(getRoleSortWeight(r)));
    }
    return Math.min(...weights);
  };

  const getMemberIdNumber = (id?: string | null): number => {
    if (!id) return 999999;
    const match = id.match(/\d+/);
    return match ? parseInt(match[0], 10) : 999999;
  };

  const getBengkungRankWeight = (bengkungVal: string | undefined, ranks: BengkungRankInfo[]): number => {
    if (!bengkungVal) return -1;
    const clean = bengkungVal.trim().toLowerCase();
    if (!clean || clean === 'tiada') return -1;

    // 1. Direct match with dbCode, code, bengkungName or formatted string
    const idx = ranks.findIndex(r =>
      r.dbCode.toLowerCase() === clean ||
      r.code.toLowerCase() === clean ||
      r.bengkungName.toLowerCase() === clean ||
      getFormattedBengkungOption(r).toLowerCase() === clean ||
      (r.rankTitle && r.rankTitle.toLowerCase() === clean)
    );
    if (idx !== -1) return idx;

    // 2. Normalized alphanumeric match: e.g. "putih0" or "putih 0 (pesilat asas)" -> "putih0"
    const cleanAlnum = clean.replace(/\s*\([^)]*\)/g, '').replace(/[^a-z0-9]/g, '');
    const normIdx = ranks.findIndex(r => {
      const rDb = r.dbCode.toLowerCase().replace(/[^a-z0-9]/g, '');
      const rCode = r.code.toLowerCase().replace(/[^a-z0-9]/g, '');
      return rDb === cleanAlnum || rCode === cleanAlnum;
    });
    if (normIdx !== -1) return normIdx;

    // 3. Fallback color and degree detection if custom string
    if (clean.includes('guru utama')) return 999;
    if (clean.includes('biru')) return 200;
    if (clean.includes('kuning')) {
      const cula = clean.match(/\d+/);
      return 100 + (cula ? parseInt(cula[0], 10) : 0);
    }
    if (clean.includes('merah')) {
      const cula = clean.match(/\d+/);
      return 50 + (cula ? parseInt(cula[0], 10) : 0);
    }
    if (clean.includes('putih')) {
      const cula = clean.match(/\d+/);
      return 20 + (cula ? parseInt(cula[0], 10) : 0);
    }
    if (clean.includes('hitam')) {
      const cula = clean.match(/\d+/);
      return 10 + (cula ? parseInt(cula[0], 10) : 0);
    }

    return -1;
  };

  const sortedMembers = useMemo(() => {
    return [...filteredMembers].sort((a, b) => {
      // 1. Default sorting: Organizational leadership hierarchy first (Pengerusi, Timbalan, Setiausaha, etc.)
      if (directorySortBy === 'default') {
        const weightA = getMemberSortWeight(a);
        const weightB = getMemberSortWeight(b);
        if (weightA !== weightB) {
          return weightA - weightB;
        }

        const timeA = a.joinedDate ? new Date(a.joinedDate).getTime() : 0;
        const timeB = b.joinedDate ? new Date(b.joinedDate).getTime() : 0;
        const validTimeA = isNaN(timeA) ? 0 : timeA;
        const validTimeB = isNaN(timeB) ? 0 : timeB;

        if (validTimeA !== validTimeB) return validTimeA - validTimeB;
        return a.name.localeCompare(b.name, 'ms', { sensitivity: 'base' });
      }

      // If user chooses other sorting options, disregard default leadership rule:

      // 2. Sort by Member ID (Menaik / Menurun)
      if (directorySortBy === 'id_asc') {
        const numA = getMemberIdNumber(a.id);
        const numB = getMemberIdNumber(b.id);
        if (numA !== numB) return numA - numB;
        return (a.id || '').localeCompare(b.id || '', undefined, { numeric: true });
      }
      if (directorySortBy === 'id_desc') {
        const numA = getMemberIdNumber(a.id);
        const numB = getMemberIdNumber(b.id);
        if (numA !== numB) return numB - numA;
        return (b.id || '').localeCompare(a.id || '', undefined, { numeric: true });
      }

      // 3. Sort by Age / Umur (Tertua / Termuda)
      if (directorySortBy === 'age_desc') {
        const ageA = calculateAge(a.icNo);
        const ageB = calculateAge(b.icNo);
        if (ageA > 0 && ageB > 0) {
          if (ageA !== ageB) return ageB - ageA;
        } else if (ageA > 0) {
          return -1;
        } else if (ageB > 0) {
          return 1;
        }
        return a.name.localeCompare(b.name, 'ms', { sensitivity: 'base' });
      }
      if (directorySortBy === 'age_asc') {
        const ageA = calculateAge(a.icNo);
        const ageB = calculateAge(b.icNo);
        if (ageA > 0 && ageB > 0) {
          if (ageA !== ageB) return ageA - ageB;
        } else if (ageA > 0) {
          return -1;
        } else if (ageB > 0) {
          return 1;
        }
        return a.name.localeCompare(b.name, 'ms', { sensitivity: 'base' });
      }

      // 4. Sort by Bengkung rank (Tertinggi / Terendah)
      if (directorySortBy === 'bengkung_desc') {
        const rankA = getBengkungRankWeight(a.bengkung, bengkungRanksList);
        const rankB = getBengkungRankWeight(b.bengkung, bengkungRanksList);
        if (rankA !== rankB) return rankB - rankA;
        return a.name.localeCompare(b.name, 'ms', { sensitivity: 'base' });
      }
      if (directorySortBy === 'bengkung_asc') {
        const rankA = getBengkungRankWeight(a.bengkung, bengkungRanksList);
        const rankB = getBengkungRankWeight(b.bengkung, bengkungRanksList);
        if (rankA >= 0 && rankB >= 0) {
          if (rankA !== rankB) return rankA - rankB;
        } else if (rankA >= 0) {
          return -1;
        } else if (rankB >= 0) {
          return 1;
        }
        return a.name.localeCompare(b.name, 'ms', { sensitivity: 'base' });
      }

      // 5. Sort by Name (A-Z / Z-A)
      if (directorySortBy === 'name') {
        return a.name.localeCompare(b.name, 'ms', { sensitivity: 'base' });
      }
      if (directorySortBy === 'name_desc') {
        return b.name.localeCompare(a.name, 'ms', { sensitivity: 'base' });
      }

      // 6. Sort by Joining Date (Terawal / Terkini)
      if (directorySortBy === 'newest') {
        const timeA = a.joinedDate ? new Date(a.joinedDate).getTime() : 0;
        const timeB = b.joinedDate ? new Date(b.joinedDate).getTime() : 0;
        const validTimeA = isNaN(timeA) ? 0 : timeA;
        const validTimeB = isNaN(timeB) ? 0 : timeB;
        if (validTimeA !== validTimeB) return validTimeB - validTimeA;
        return a.name.localeCompare(b.name, 'ms', { sensitivity: 'base' });
      }
      if (directorySortBy === 'oldest') {
        const timeA = a.joinedDate ? new Date(a.joinedDate).getTime() : 0;
        const timeB = b.joinedDate ? new Date(b.joinedDate).getTime() : 0;
        const validTimeA = isNaN(timeA) ? 0 : timeA;
        const validTimeB = isNaN(timeB) ? 0 : timeB;
        if (validTimeA !== validTimeB) return validTimeA - validTimeB;
        return a.name.localeCompare(b.name, 'ms', { sensitivity: 'base' });
      }

      return a.name.localeCompare(b.name, 'ms', { sensitivity: 'base' });
    });
  }, [filteredMembers, directorySortBy, bengkungRanksList]);

  const totalPages = Math.max(1, Math.ceil(sortedMembers.length / itemsPerPage));
  const validCurrentPage = Math.min(currentPage, totalPages);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  const paginatedMembers = useMemo(() => {
    const startIndex = (validCurrentPage - 1) * itemsPerPage;
    return sortedMembers.slice(startIndex, startIndex + itemsPerPage);
  }, [sortedMembers, validCurrentPage]);

  const isExecutive = Boolean(currentUser && isUserManagement(currentUser));

  const getRoleBadgeStyle = (userRole: Role | string) => {
    if (!userRole) return 'bg-slate-100 text-slate-700 border-slate-200 font-bold';
    if (userRole.startsWith('Ketua Biro') || userRole.includes('Biro')) {
      return 'bg-amber-100 text-amber-900 border-amber-300 font-extrabold';
    }
    switch (userRole) {
      case 'Pengerusi':
      case 'Timbalan Pengerusi':
        return 'bg-amber-100 text-amber-900 border-amber-300 font-extrabold';
      case 'Setiausaha':
      case 'Pen. Setiausaha':
      case 'Penolong Setiausaha':
      case 'Bendahari':
      case 'Penolong Bendahari':
      case 'Pen. Bendahari':
        return 'bg-blue-100 text-blue-900 border-blue-300 font-extrabold';
      case 'Juru Audit Dalaman':
      case 'Juruaudit Dalaman':
      case 'Juru Audit':
      case 'Pemeriksa Kira-Kira':
        return 'bg-indigo-100 text-indigo-900 border-indigo-300 font-extrabold';
      case 'Ketua Gelanggang':
      case 'Bendahari Gelanggang':
        return 'bg-purple-100 text-purple-900 border-purple-300 font-extrabold';
      case 'AJK':
        return 'bg-emerald-100 text-emerald-900 border-emerald-300 font-extrabold';
      case 'Ahli Seumur Hidup':
        return 'bg-amber-50 text-amber-900 border-amber-300 font-extrabold';
      case 'Waris':
        return 'bg-slate-900 text-white border-slate-800 font-extrabold';
      case 'Ahli':
      default:
        return 'bg-slate-100 text-slate-700 border-slate-200 font-bold';
    }
  };

  return (
    <div className="w-full space-y-6 min-h-[75vh]">
      {/* Page Header */}
      <PageHeader
        badgeText={activeSubTab === 'daftar' ? (registrationType === 'waris' ? "Borang Pendaftaran Waris" : "Borang Pendaftaran Pesilat") : activeSubTab === 'baru' ? "Kelulusan Pendaftaran" : "Pendaftaran Ahli"}
        badgeIcon={activeSubTab === 'daftar' ? (registrationType === 'waris' ? Users : UserPlus) : activeSubTab === 'baru' ? UserPlus : Users}
        title={activeSubTab === 'daftar' ? (registrationType === 'waris' ? "Daftar Waris Baharu" : "Daftar Pesilat Baharu") : activeSubTab === 'baru' ? "Kelulusan Ahli Baharu" : "Direktori Ahli"}
        titleBadge={activeSubTab === 'daftar' ? undefined : activeSubTab === 'baru' ? myPending.length : filteredMembers.length}
      />

      {/* Sub-Tabs Switcher Bar */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 bg-white p-3 sm:p-4 rounded-3xl border border-slate-200 shadow-2xs">
        <div className="w-full sm:w-fit flex items-center gap-2 p-1 bg-slate-100 rounded-2xl border border-slate-200/80 overflow-x-auto no-scrollbar flex-nowrap">
          <button
            onClick={() => handleSubTabChange('direktori')}
            className={`py-2 px-3.5 sm:px-4 rounded-xl text-xs font-extrabold transition-all flex items-center gap-2 cursor-pointer border-none shrink-0 whitespace-nowrap ${activeSubTab === 'direktori'
              ? 'bg-white text-asmm-red shadow-sm'
              : 'text-slate-600 hover:text-slate-900'
              }`}
          >
            <Users className="w-4 h-4 shrink-0" />
            <span>Direktori Ahli</span>
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-black ${activeSubTab === 'direktori' ? 'bg-red-100 text-asmm-red' : 'bg-slate-200 text-slate-600'
              }`}>
              {filteredMembers.length}
            </span>
          </button>

          {showApprovalPanel && (
            <button
              onClick={() => handleSubTabChange('baru')}
              className={`py-2 px-3.5 sm:px-4 rounded-xl text-xs font-extrabold transition-all flex items-center gap-2 cursor-pointer border-none shrink-0 whitespace-nowrap ${activeSubTab === 'baru'
                ? 'bg-white text-asmm-red shadow-sm'
                : 'text-slate-600 hover:text-slate-900'
                }`}
            >
              <UserPlus className="w-4 h-4 shrink-0" />
              <span>Ahli Baru</span>
              {myPending.length > 0 ? (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full font-black bg-amber-500 text-white animate-pulse">
                  {myPending.length}
                </span>
              ) : (
                <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-bold ${activeSubTab === 'baru' ? 'bg-red-100 text-asmm-red' : 'bg-slate-200 text-slate-500'
                  }`}>
                  0
                </span>
              )}
            </button>
          )}

          {isExecutive && (
            <>
              <button
                onClick={() => handleSubTabChange('daftar', 'pesilat')}
                className={`py-2 px-3.5 sm:px-4 rounded-xl text-xs font-extrabold transition-all flex items-center gap-2 cursor-pointer border-none shrink-0 whitespace-nowrap ${activeSubTab === 'daftar' && registrationType === 'pesilat'
                  ? 'bg-white text-asmm-red shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
                  }`}
              >
                <UserPlus className="w-4 h-4 shrink-0" />
                <span>Daftar Pesilat</span>
              </button>

              <button
                onClick={() => handleSubTabChange('daftar', 'waris')}
                className={`py-2 px-3.5 sm:px-4 rounded-xl text-xs font-extrabold transition-all flex items-center gap-2 cursor-pointer border-none shrink-0 whitespace-nowrap ${activeSubTab === 'daftar' && registrationType === 'waris'
                  ? 'bg-white text-slate-900 shadow-sm'
                  : 'text-slate-600 hover:text-slate-900'
                  }`}
              >
                <Users className="w-4 h-4 text-amber-500 shrink-0" />
                <span>Daftar Waris</span>
              </button>
            </>
          )}

          {isSuperAdmin(currentUser) && (
            <button
              type="button"
              onClick={() => {
                window.history.pushState(null, '', '/roles');
                window.dispatchEvent(new PopStateEvent('popstate'));
              }}
              className="py-2 px-3.5 sm:px-4 rounded-xl text-xs font-extrabold transition-all flex items-center gap-2 cursor-pointer border-none shrink-0 whitespace-nowrap text-slate-600 hover:text-slate-900 hover:bg-white/80"
              title="Buka Halaman Roles Management"
            >
              <ShieldCheck className="w-4 h-4 text-amber-600 shrink-0" />
              <span>Roles Management</span>
            </button>
          )}
        </div>

        {activeSubTab !== 'daftar' && isExecutive && (
          <div className="flex items-center gap-2">
            <button
              onClick={() => handleSubTabChange('daftar', 'pesilat')}
              className="px-3.5 py-2 bg-asmm-red hover:bg-asmm-crimson text-white text-xs font-bold rounded-xl shadow-sm transition-all flex items-center gap-1.5 shrink-0 cursor-pointer border-none"
            >
              <UserPlus className="w-3.5 h-3.5" />
              <span>Daftar Pesilat</span>
            </button>
            <button
              onClick={() => handleSubTabChange('daftar', 'waris')}
              className="px-3.5 py-2 bg-slate-900 hover:bg-slate-800 text-white text-xs font-bold rounded-xl shadow-sm transition-all flex items-center gap-1.5 shrink-0 cursor-pointer border-none"
            >
              <Users className="w-3.5 h-3.5 text-amber-400" />
              <span>Daftar Waris</span>
            </button>
          </div>
        )}
      </div>

      {/* SUBTAB 1: AHLI BARU (KELULUSAN AHLI BAHARU PAGEVIEW) */}
      {activeSubTab === 'baru' && (
        <div className="space-y-6 animate-fade-in">
          {/* Header Banner & Summary KPI Metrics for Ahli Baru */}
          <div className="bg-gradient-to-br from-amber-500/10 via-amber-500/5 to-white rounded-3xl p-5 sm:p-6 border border-amber-200/80 shadow-xs flex flex-col lg:flex-row lg:items-center justify-between gap-5">
            <div className="flex items-start gap-3.5">
              <div className="p-3 bg-amber-500 text-white rounded-2xl shrink-0 shadow-xs mt-0.5">
                <UserPlus className="w-6 h-6" />
              </div>
              <div className="space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="text-base sm:text-lg font-black text-slate-900">
                    Pengurusan & Kelulusan Permohonan Ahli
                  </h3>
                  <span className="text-[10px] font-black bg-amber-500 text-white px-2.5 py-0.5 rounded-full uppercase tracking-wider">
                    {myPending.length} Menunggu
                  </span>
                </div>
                <p className="text-xs text-slate-600 max-w-2xl leading-relaxed">
                  Semak maklumat pesilat baharu, luluskan keahlian dengan rekod audit rasmi, dan pantau sejarah kelulusan tanpa kehilangan rekod permohonan.
                </p>
              </div>
            </div>

            {/* Quick Gelanggang Breakdown Chips */}
            <div className="flex items-center gap-2 flex-wrap self-start lg:self-center">
              <button
                type="button"
                onClick={() => setPendingGelanggangFilter('all')}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer border ${pendingGelanggangFilter === 'all'
                  ? 'bg-amber-600 text-white border-amber-600 shadow-2xs'
                  : 'bg-white text-slate-700 border-amber-200/80 hover:bg-amber-50'
                  }`}
              >
                <span>Semua</span>
                <span className={`px-1.5 py-0.2 rounded-md text-[10px] font-black ${pendingGelanggangFilter === 'all' ? 'bg-amber-700 text-white' : 'bg-amber-100 text-amber-900'
                  }`}>
                  {pendingCountsByGelanggang.all}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setPendingGelanggangFilter('Pusaka Warisan')}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer border ${pendingGelanggangFilter === 'Pusaka Warisan'
                  ? 'bg-amber-600 text-white border-amber-600 shadow-2xs'
                  : 'bg-white text-slate-700 border-amber-200/80 hover:bg-amber-50'
                  }`}
              >
                <span>Pusaka Warisan</span>
                <span className={`px-1.5 py-0.2 rounded-md text-[10px] font-black ${pendingGelanggangFilter === 'Pusaka Warisan' ? 'bg-amber-700 text-white' : 'bg-amber-100 text-amber-900'
                  }`}>
                  {pendingCountsByGelanggang['Pusaka Warisan']}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setPendingGelanggangFilter('Wadi AnNour')}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer border ${pendingGelanggangFilter === 'Wadi AnNour'
                  ? 'bg-amber-600 text-white border-amber-600 shadow-2xs'
                  : 'bg-white text-slate-700 border-amber-200/80 hover:bg-amber-50'
                  }`}
              >
                <span>Wadi AnNour</span>
                <span className={`px-1.5 py-0.2 rounded-md text-[10px] font-black ${pendingGelanggangFilter === 'Wadi AnNour' ? 'bg-amber-700 text-white' : 'bg-amber-100 text-amber-900'
                  }`}>
                  {pendingCountsByGelanggang['Wadi AnNour']}
                </span>
              </button>

              <button
                type="button"
                onClick={() => setPendingGelanggangFilter('Paya Besar')}
                className={`px-3 py-1.5 rounded-xl text-xs font-bold transition-all flex items-center gap-1.5 cursor-pointer border ${pendingGelanggangFilter === 'Paya Besar'
                  ? 'bg-amber-600 text-white border-amber-600 shadow-2xs'
                  : 'bg-white text-slate-700 border-amber-200/80 hover:bg-amber-50'
                  }`}
              >
                <span>Paya Besar</span>
                <span className={`px-1.5 py-0.2 rounded-md text-[10px] font-black ${pendingGelanggangFilter === 'Paya Besar' ? 'bg-amber-700 text-white' : 'bg-amber-100 text-amber-900'
                  }`}>
                  {pendingCountsByGelanggang['Paya Besar']}
                </span>
              </button>
            </div>
          </div>

          {/* Status Sub-Tabs Switcher Bar */}
          <div className="flex flex-wrap items-center gap-2 p-1.5 bg-white rounded-2xl border border-slate-200/90 shadow-2xs">
            <button
              type="button"
              onClick={() => handlePendingStatusTabChange('menunggu')}
              className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer border-none ${pendingStatusTab === 'menunggu'
                ? 'bg-amber-500 text-white shadow-xs font-extrabold'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                }`}
            >
              <Clock className="w-4 h-4" />
              <span>Menunggu Kelulusan</span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${pendingStatusTab === 'menunggu' ? 'bg-amber-700 text-white' : 'bg-amber-100 text-amber-900'
                }`}>
                {myPending.length}
              </span>
            </button>

            <button
              type="button"
              onClick={() => handlePendingStatusTabChange('diluluskan')}
              className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer border-none ${pendingStatusTab === 'diluluskan'
                ? 'bg-emerald-600 text-white shadow-xs font-extrabold'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                }`}
            >
              <CheckCircle2 className="w-4 h-4" />
              <span>Telah Diluluskan</span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${pendingStatusTab === 'diluluskan' ? 'bg-emerald-800 text-white' : 'bg-emerald-100 text-emerald-900'
                }`}>
                {myApproved.length}
              </span>
            </button>

            <button
              type="button"
              onClick={() => handlePendingStatusTabChange('ditolak')}
              className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer border-none ${pendingStatusTab === 'ditolak'
                ? 'bg-rose-600 text-white shadow-xs font-extrabold'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                }`}
            >
              <UserX className="w-4 h-4" />
              <span>Permohonan Ditolak</span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${pendingStatusTab === 'ditolak' ? 'bg-rose-800 text-white' : 'bg-rose-100 text-rose-900'
                }`}>
                {myRejected.length}
              </span>
            </button>

            <button
              type="button"
              onClick={() => handlePendingStatusTabChange('log')}
              className={`px-4 py-2.5 rounded-xl text-xs font-bold transition-all flex items-center gap-2 cursor-pointer border-none ${pendingStatusTab === 'log'
                ? 'bg-blue-600 text-white shadow-xs font-extrabold'
                : 'text-slate-600 hover:text-slate-900 hover:bg-slate-100'
                }`}
            >
              <History className="w-4 h-4" />
              <span>Log Audit Kelulusan</span>
              <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${pendingStatusTab === 'log' ? 'bg-blue-800 text-white' : 'bg-blue-100 text-blue-900'
                }`}>
                {auditLogItems.length}
              </span>
            </button>
          </div>

          {/* Search, Filter & Sorting Bar */}
          <div className="bg-white p-4 rounded-3xl border border-slate-200 shadow-2xs flex flex-col md:flex-row items-stretch md:items-center justify-between gap-3">
            {/* Search input */}
            <div className="relative flex-1">
              <Search className="absolute left-3.5 top-2.5 w-4 h-4 text-slate-400" />
              <input
                type="text"
                value={pendingSearchTerm}
                onChange={(e) => setPendingSearchTerm(e.target.value)}
                placeholder={
                  pendingStatusTab === 'log'
                    ? "Cari nama pemohon, pegawai penilai, IC, atau gelanggang..."
                    : "Cari nama, No. IC, telefon, emel, pegawai penilai, waris..."
                }
                className="w-full pl-10 pr-9 py-2 rounded-2xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 placeholder-slate-400 font-medium transition-all"
              />
              {pendingSearchTerm && (
                <button
                  type="button"
                  onClick={() => setPendingSearchTerm('')}
                  className="absolute right-3 top-2.5 text-slate-400 hover:text-slate-600 p-0.5"
                  title="Kosongkan carian"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </div>

            {/* Sort & Quick Actions */}
            <div className="flex items-center gap-2 flex-wrap shrink-0">
              <div className="flex items-center gap-1.5 bg-slate-50 px-3 py-1.5 rounded-2xl border border-slate-200 text-xs">
                <ArrowUpDown className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                <span className="text-slate-500 font-bold hidden sm:inline">Susun:</span>
                <select
                  value={pendingSortBy}
                  onChange={(e) => setPendingSortBy(e.target.value as any)}
                  className="bg-transparent text-xs font-bold text-slate-800 focus:outline-none cursor-pointer border-none"
                >
                  <option value="newest">Terkini (Tarikh)</option>
                  <option value="oldest">Terlama (Tarikh)</option>
                  <option value="name">Nama Pesilat (A - Z)</option>
                  <option value="gelanggang">Gelanggang</option>
                </select>
              </div>

              {pendingStatusTab === 'menunggu' && filteredPending.length > 0 && (
                <button
                  type="button"
                  onClick={handleSelectAllPending}
                  className="px-3 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-2xl transition-colors flex items-center gap-1.5 cursor-pointer border-none"
                >
                  {selectedPendingIds.length === filteredPending.length && filteredPending.length > 0 ? (
                    <>
                      <CheckSquare className="w-3.5 h-3.5 text-asmm-red" />
                      <span>Nyahpilih Semua</span>
                    </>
                  ) : (
                    <>
                      <Square className="w-3.5 h-3.5 text-slate-500" />
                      <span>Pilih Semua ({filteredPending.length})</span>
                    </>
                  )}
                </button>
              )}
            </div>
          </div>

          {/* Bulk Action floating/sticky bar when items are selected (Menunggu Kelulusan) */}
          {pendingStatusTab === 'menunggu' && selectedPendingIds.length > 0 && (
            <div className="bg-slate-900 text-white p-3.5 sm:p-4 rounded-2xl shadow-lg border border-slate-800 flex flex-col sm:flex-row items-center justify-between gap-3 animate-fade-in">
              <div className="flex items-center gap-2.5">
                <span className="w-6 h-6 rounded-full bg-amber-500 text-slate-950 font-black text-xs flex items-center justify-center">
                  {selectedPendingIds.length}
                </span>
                <span className="text-xs font-bold text-slate-100">
                  {selectedPendingIds.length} permohonan pesilat dipilih
                </span>
              </div>

              <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
                <button
                  type="button"
                  onClick={() => setSelectedPendingIds([])}
                  className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 text-xs font-bold rounded-xl transition-colors cursor-pointer border-none"
                >
                  Batal
                </button>
                <button
                  type="button"
                  onClick={() => setShowBulkRejectModal(true)}
                  disabled={isBulkApproving || isBulkRejecting}
                  className="px-3 py-1.5 bg-rose-600/20 hover:bg-rose-600/30 text-rose-300 border border-rose-500/40 text-xs font-bold rounded-xl transition-colors flex items-center gap-1.5 cursor-pointer disabled:opacity-50"
                >
                  <UserX className="w-3.5 h-3.5" />
                  <span>Tolak Terpilih ({selectedPendingIds.length})</span>
                </button>
                <button
                  type="button"
                  onClick={handleBulkApprove}
                  disabled={isBulkApproving || isBulkRejecting}
                  className="px-4 py-1.5 bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-extrabold rounded-xl transition-all shadow-sm flex items-center gap-1.5 cursor-pointer disabled:opacity-50 active:scale-95"
                >
                  <CheckCircle2 className="w-4 h-4" />
                  <span>{isBulkApproving ? 'Memproses...' : `Luluskan Terpilih (${selectedPendingIds.length})`}</span>
                </button>
              </div>
            </div>
          )}

          {/* VIEW MODE 1: AUDIT LOG VIEW (TAB === 'log') */}
          {pendingStatusTab === 'log' ? (
            <div className="space-y-4 animate-fade-in">
              {sortedFilteredPending.length > 0 ? (
                <div className="bg-white rounded-3xl border border-slate-200/90 shadow-2xs overflow-hidden">
                  <div className="p-4 sm:p-5 border-b border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-slate-50/50">
                    <div className="flex items-center gap-2.5">
                      <div className="p-2 bg-blue-100 text-blue-700 rounded-xl">
                        <History className="w-4 h-4" />
                      </div>
                      <div>
                        <h4 className="text-sm font-extrabold text-slate-900">Rekod Audit Kelulusan Keahlian</h4>
                        <p className="text-xs text-slate-500">Log rasmi pegawai pengurusan yang meluluskan atau menolak pendaftaran pesilat ASMM.</p>
                      </div>
                    </div>
                    <span className="text-xs font-bold text-slate-600 bg-white px-3 py-1 rounded-xl border border-slate-200 shrink-0">
                      {sortedFilteredPending.length} Rekod Terpilih
                    </span>
                  </div>

                  <div className="overflow-x-auto">
                    <table className="w-full text-left border-collapse text-xs">
                      <thead>
                        <tr className="bg-slate-50/80 text-slate-500 font-extrabold border-b border-slate-200/80 text-[11px] uppercase tracking-wider">
                          <th className="py-3 px-4">Tarikh & Masa</th>
                          <th className="py-3 px-4">Nama Pemohon</th>
                          <th className="py-3 px-4">Gelanggang / Bengkung</th>
                          <th className="py-3 px-4">Status</th>
                          <th className="py-3 px-4">Pegawai Pengesah</th>
                          <th className="py-3 px-4 text-right">Tindakan</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {sortedFilteredPending.map((m) => {
                          const dateDisplay = m.approvedAt
                            ? formatDateTime(m.approvedAt)
                            : (m.rejectedAt ? formatDateTime(m.rejectedAt) : formatSubmissionDate(m.joinedDate));

                          return (
                            <tr key={m.id} className="hover:bg-slate-50/70 transition-colors">
                              <td className="py-3.5 px-4 font-mono font-medium text-slate-600 whitespace-nowrap">
                                <div className="flex items-center gap-1.5">
                                  <Clock className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                  <span>{dateDisplay}</span>
                                </div>
                              </td>

                              <td className="py-3.5 px-4">
                                <div className="flex items-center gap-2.5 min-w-0">
                                  <img
                                    src={m.photoUrl || '/default-avatar.svg'}
                                    alt={m.name}
                                    onError={(e) => { (e.target as HTMLElement).setAttribute('src', '/default-avatar.svg'); }}
                                    className="w-8 h-8 rounded-xl object-cover border border-slate-200 shrink-0"
                                  />
                                  <div className="min-w-0">
                                    <div className="font-extrabold text-slate-900 truncate">{m.name}</div>
                                    <div className="text-[11px] font-mono text-slate-500 truncate flex items-center gap-1.5 flex-wrap">
                                      <span className="font-bold text-slate-700 bg-slate-100 px-1 rounded border border-slate-200 text-[10px]">{sanitizeMemberId(m.id)}</span>
                                      <span>•</span>
                                      <span>{renderIcNo(m) ? `IC: ${m.icNo} • ${m.phone || '-'}` : ((m.gelanggang === 'Semua Gelanggang' ? 'ASMM' : m.gelanggang) || 'ASMM')}</span>
                                    </div>
                                  </div>
                                </div>
                              </td>

                              <td className="py-3.5 px-4 whitespace-nowrap">
                                <div className="space-y-0.5">
                                  <div className="font-bold text-slate-800 flex items-center gap-1">
                                    <MapPin className="w-3 h-3 text-asmm-red shrink-0" />
                                    <span>{(m.gelanggang === 'Semua Gelanggang' ? 'ASMM' : m.gelanggang) || 'Belum Ditetapkan'}</span>
                                  </div>
                                  <div className="text-[11px] text-slate-500">
                                    {getSilatRankFromBengkung(m.bengkung || 'Putih')}
                                  </div>
                                </div>
                              </td>

                              <td className="py-3.5 px-4 whitespace-nowrap">
                                {m.status === 'Aktif' ? (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-extrabold bg-emerald-100 text-emerald-800 border border-emerald-200">
                                    <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                                    <span>Diluluskan</span>
                                  </span>
                                ) : m.status === 'Ditolak' ? (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-extrabold bg-rose-100 text-rose-800 border border-rose-200">
                                    <UserX className="w-3 h-3 text-rose-600" />
                                    <span>Ditolak</span>
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-extrabold bg-amber-100 text-amber-800 border border-amber-200 animate-pulse">
                                    <Clock className="w-3 h-3 text-amber-600" />
                                    <span>Menunggu Semakan</span>
                                  </span>
                                )}
                              </td>

                              <td className="py-3.5 px-4 whitespace-nowrap">
                                {m.approvedByName ? (
                                  <div className="flex items-center gap-1.5 text-slate-900 font-bold">
                                    <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />
                                    <div>
                                      <span>{m.approvedByName}</span>
                                      <span className="block text-[10px] font-medium text-emerald-700">Diluluskan</span>
                                    </div>
                                  </div>
                                ) : m.rejectedByName ? (
                                  <div className="flex items-center gap-1.5 text-slate-900 font-bold">
                                    <UserX className="w-4 h-4 text-rose-600 shrink-0" />
                                    <div>
                                      <span>{m.rejectedByName}</span>
                                      <span className="block text-[10px] font-medium text-rose-700">Ditolak</span>
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-slate-400 italic font-medium">Belum Diproses</span>
                                )}
                              </td>

                              <td className="py-3.5 px-4 text-right whitespace-nowrap">
                                <div className="flex items-center justify-end gap-1.5">
                                  <button
                                    type="button"
                                    onClick={() => setPreviewPendingMember(m)}
                                    className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl text-xs font-bold transition-colors inline-flex items-center gap-1 cursor-pointer border-none"
                                  >
                                    <Eye className="w-3.5 h-3.5 text-slate-500" />
                                    <span>Semak</span>
                                  </button>
                                  {isSuperAdmin(currentUser) && (
                                    <button
                                      type="button"
                                      onClick={() => handleDeleteMember(m.id, m.name)}
                                      className="p-1.5 text-rose-600 hover:text-rose-800 hover:bg-rose-50 rounded-xl transition-colors cursor-pointer border border-transparent hover:border-rose-200"
                                      title="Padam baris log audit (Superadmin sahaja)"
                                    >
                                      <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </div>
              ) : (
                <div className="bg-white rounded-3xl border border-slate-200 shadow-2xs p-10 sm:p-14 text-center space-y-4">
                  <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-3xl flex items-center justify-center mx-auto border border-blue-200 shadow-xs">
                    <History className="w-8 h-8" />
                  </div>
                  <div className="space-y-1.5 max-w-md mx-auto">
                    <h4 className="text-base sm:text-lg font-black text-slate-900">
                      Tiada Rekod Audit Ditemui
                    </h4>
                    <p className="text-xs text-slate-500 leading-relaxed">
                      Belum ada sebarang aktiviti audit kelulusan atau penolakan permohonan yang sepadan dengan tapisan semasa.
                    </p>
                  </div>
                </div>
              )}
            </div>
          ) : (
            /* VIEW MODE 2: CARDS VIEW (TAB === 'menunggu' | 'diluluskan' | 'ditolak') */
            sortedFilteredPending.length > 0 ? (
              <div className="space-y-4">
                {sortedFilteredPending.map((m) => {
                  const icInfo = getIcDetails(m.icNo);
                  const isSelected = selectedPendingIds.includes(m.id);

                  return (
                    <div
                      key={m.id}
                      className={`bg-white rounded-3xl border transition-all overflow-hidden flex flex-col ${isSelected
                        ? 'border-amber-400 ring-2 ring-amber-400/20 shadow-md'
                        : m.status === 'Ditolak'
                          ? 'border-rose-200 shadow-2xs hover:border-rose-300'
                          : m.status === 'Aktif'
                            ? 'border-emerald-200 shadow-2xs hover:border-emerald-300'
                            : 'border-slate-200/90 shadow-2xs hover:border-slate-300 hover:shadow-sm'
                        }`}
                    >
                      {/* Card Header Bar */}
                      <div className={`px-4 sm:px-6 py-3.5 border-b flex flex-col sm:flex-row sm:items-center justify-between gap-3 ${m.status === 'Ditolak'
                        ? 'bg-gradient-to-r from-rose-50/80 via-slate-50/50 to-white border-rose-100'
                        : m.status === 'Aktif'
                          ? 'bg-gradient-to-r from-emerald-50/80 via-slate-50/50 to-white border-emerald-100'
                          : 'bg-gradient-to-r from-amber-50/80 via-slate-50/50 to-white border-slate-100'
                        }`}>
                        <div className="flex items-center gap-3 min-w-0">
                          {/* Checkbox (Only for Menunggu) */}
                          {pendingStatusTab === 'menunggu' && (
                            <button
                              type="button"
                              onClick={() => handleToggleSelectPending(m.id)}
                              className="p-1 text-slate-400 hover:text-slate-600 cursor-pointer rounded-lg hover:bg-white/80 transition-colors"
                              title={isSelected ? 'Nyahpilih' : 'Pilih permohonan ini'}
                            >
                              {isSelected ? (
                                <CheckSquare className="w-5 h-5 text-amber-600" />
                              ) : (
                                <Square className="w-5 h-5 text-slate-300" />
                              )}
                            </button>
                          )}

                          <img
                            src={m.photoUrl || '/default-avatar.svg'}
                            alt={m.name}
                            onError={(e) => { (e.target as HTMLElement).setAttribute('src', '/default-avatar.svg'); }}
                            className="w-11 h-11 sm:w-12 sm:h-12 rounded-2xl object-cover border border-slate-200 shadow-2xs shrink-0"
                          />

                          <div className="min-w-0 flex-1">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="text-sm sm:text-base font-black text-slate-900 truncate">
                                {m.name}
                              </h4>
                              <span className="text-[10px] font-mono font-bold text-slate-700 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200">
                                {sanitizeMemberId(m.id)}
                              </span>
                              {((m.roles && m.roles.length > 0) ? m.roles : [m.role || 'Ahli']).map(r => (
                                <span key={r} className="text-[10px] font-extrabold text-amber-800 bg-amber-100/90 px-2 py-0.5 rounded-md border border-amber-200/60">
                                  {r}
                                </span>
                              ))}
                              {icInfo && (
                                <span className="text-[10px] font-bold text-slate-600 bg-slate-100 px-2 py-0.5 rounded-md border border-slate-200">
                                  {icInfo.category} ({icInfo.age} Thn) {icInfo.gender ? `• ${icInfo.gender}` : ''}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Status & Audit Tag */}
                        <div className="flex items-center gap-2 self-start sm:self-center pl-8 sm:pl-0 shrink-0 flex-wrap">
                          {m.status === 'Aktif' && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-extrabold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-xl">
                              <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                              <span>Diluluskan</span>
                            </span>
                          )}
                          {m.status === 'Ditolak' && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-extrabold text-rose-700 bg-rose-50 border border-rose-200 px-2.5 py-1 rounded-xl">
                              <UserX className="w-3 h-3 text-rose-600" />
                              <span>Ditolak</span>
                            </span>
                          )}
                          {m.status === 'Menunggu Pengesahan' && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-extrabold text-amber-700 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded-xl">
                              <Clock className="w-3 h-3 text-amber-600 animate-pulse" />
                              <span>Menunggu Kelulusan</span>
                            </span>
                          )}

                          {m.approvedByName && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-emerald-800 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-xl">
                              <ShieldCheck className="w-3 h-3 text-emerald-600" />
                              <span>Oleh: {m.approvedByName}</span>
                            </span>
                          )}

                          {m.rejectedByName && (
                            <span className="inline-flex items-center gap-1 text-[11px] font-bold text-rose-800 bg-rose-50 border border-rose-200 px-2.5 py-1 rounded-xl">
                              <UserX className="w-3 h-3 text-rose-600" />
                              <span>Oleh: {m.rejectedByName}</span>
                            </span>
                          )}

                          <span className="text-[11px] font-medium text-slate-500 flex items-center gap-1 bg-slate-100/80 px-2.5 py-1 rounded-xl border border-slate-200">
                            <Calendar className="w-3 h-3 text-slate-400" />
                            <span>Mohon: {formatSubmissionDate(m.joinedDate)}</span>
                          </span>
                        </div>
                      </div>

                      {/* Card Body: 3 Structured Panels */}
                      <div className="p-4 sm:p-5 grid grid-cols-1 md:grid-cols-3 gap-3.5">
                        {/* Panel 1: Pengenalan & Hubungan */}
                        <div className="bg-slate-50/70 rounded-2xl p-3.5 border border-slate-200/70 space-y-2 flex flex-col justify-between">
                          <div className="space-y-2">
                            <div className="flex items-center gap-1.5 text-[11px] font-extrabold text-slate-500 uppercase tracking-wider border-b border-slate-200/60 pb-1.5">
                              <User className="w-3.5 h-3.5 text-slate-400" />
                              <span>Pengenalan &amp; Keahlian</span>
                            </div>

                            {canViewMemberSensitiveInfo(currentUser, m) ? (
                              <>
                                {/* IC */}
                                <div className="text-xs flex items-center justify-between">
                                  <span className="text-slate-500 font-medium">No. IC:</span>
                                  <div className="flex items-center gap-1">
                                    <span className="font-mono font-bold text-slate-900">{m.icNo}</span>
                                    {m.icNo && (
                                      <button
                                        type="button"
                                        onClick={() => handleCopyText(m.id, 'ic', m.icNo)}
                                        className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-200 rounded-md transition-colors"
                                        title="Salin No. IC"
                                      >
                                        {copiedField?.id === m.id && copiedField?.type === 'ic' ? (
                                          <Check className="w-3 h-3 text-emerald-600" />
                                        ) : (
                                          <Copy className="w-3 h-3" />
                                        )}
                                      </button>
                                    )}
                                  </div>
                                </div>

                                {/* Phone & WhatsApp */}
                                <div className="text-xs flex items-center justify-between">
                                  <span className="text-slate-500 font-medium">Telefon:</span>
                                  {m.phone ? (
                                    <div className="flex items-center gap-1">
                                      <a
                                        href={`https://wa.me/6${m.phone.replace(/\D/g, '')}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-1 text-emerald-700 hover:text-emerald-800 bg-emerald-50 hover:bg-emerald-100 border border-emerald-200/80 px-2 py-0.5 rounded-lg font-mono font-bold transition-colors text-[11px]"
                                        title="Buka WhatsApp"
                                      >
                                        <MessageCircle className="w-3 h-3 text-emerald-600" />
                                        <span>{m.phone}</span>
                                      </a>
                                      <button
                                        type="button"
                                        onClick={() => handleCopyText(m.id, 'phone', m.phone)}
                                        className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-200 rounded-md transition-colors"
                                        title="Salin No. Telefon"
                                      >
                                        {copiedField?.id === m.id && copiedField?.type === 'phone' ? (
                                          <Check className="w-3 h-3 text-emerald-600" />
                                        ) : (
                                          <Copy className="w-3 h-3" />
                                        )}
                                      </button>
                                    </div>
                                  ) : (
                                    <span className="text-slate-400 italic font-medium">-</span>
                                  )}
                                </div>

                                {/* Email */}
                                <div className="text-xs flex items-center justify-between">
                                  <span className="text-slate-500 font-medium">Emel:</span>
                                  {m.email ? (
                                    <a
                                      href={`mailto:${m.email}`}
                                      className="text-blue-600 hover:text-blue-800 font-medium truncate max-w-[140px] hover:underline text-[11px]"
                                      title={m.email}
                                    >
                                      {m.email}
                                    </a>
                                  ) : (
                                    <span className="text-slate-400 italic font-medium">-</span>
                                  )}
                                </div>
                              </>
                            ) : (
                              <>
                                {/* Non-sensitive membership info for Waris & Ahli biasa */}
                                <div className="text-xs flex items-center justify-between">
                                  <span className="text-slate-500 font-medium">Status Keahlian:</span>
                                  <span className="inline-flex items-center gap-1 font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-lg text-[11px]">
                                    <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                                    <span>{m.status || 'Aktif'}</span>
                                  </span>
                                </div>

                                <div className="text-xs flex items-center justify-between gap-2">
                                  <span className="text-slate-500 font-medium shrink-0">Peranan:</span>
                                  <div className="flex items-center gap-1 flex-wrap justify-end">
                                    {((m.roles && m.roles.length > 0) ? m.roles : [m.role || 'Ahli']).map(r => (
                                      <span key={r} className="font-bold text-slate-800 bg-slate-100 px-1.5 py-0.5 rounded text-[10px] border border-slate-200">
                                        {r}
                                      </span>
                                    ))}
                                  </div>
                                </div>

                                <div className="text-xs flex items-center justify-between">
                                  <span className="text-slate-500 font-medium">Tarikh Sertai:</span>
                                  <span className="font-semibold text-slate-700 text-[11px]">{formatSubmissionDate(m.joinedDate)}</span>
                                </div>
                              </>
                            )}
                          </div>
                        </div>

                        {/* Panel 2: Pendaftaran Gelanggang & Silat */}
                        <div className="bg-slate-50/70 rounded-2xl p-3.5 border border-slate-200/70 space-y-2 flex flex-col justify-between">
                          <div className="space-y-2">
                            <div className="flex items-center gap-1.5 text-[11px] font-extrabold text-slate-500 uppercase tracking-wider border-b border-slate-200/60 pb-1.5">
                              <Shield className="w-3.5 h-3.5 text-asmm-red" />
                              <span>Gelanggang & Latihan</span>
                            </div>

                            {/* Gelanggang */}
                            <div className="text-xs flex items-center justify-between">
                              <span className="text-slate-500 font-medium">Gelanggang:</span>
                              <span className="inline-flex items-center gap-1 font-bold text-slate-900 bg-white px-2 py-0.5 rounded-lg border border-slate-200 text-[11px]">
                                <MapPin className="w-3 h-3 text-asmm-red shrink-0" />
                                <span className="truncate">{(m.gelanggang === 'Semua Gelanggang' ? 'ASMM' : m.gelanggang) || 'Belum Ditetapkan'}</span>
                              </span>
                            </div>

                            {/* Sesi Latihan */}
                            <div className="text-xs flex items-center justify-between">
                              <span className="text-slate-500 font-medium">Sesi Latihan:</span>
                              <span className="inline-flex items-center gap-1 font-semibold text-slate-800 text-[11px]">
                                <Clock className="w-3 h-3 text-blue-500 shrink-0" />
                                <span className="truncate">{m.sessionTime || 'Semua Sesi'}</span>
                              </span>
                            </div>

                            {/* Bengkung */}
                            <div className="text-xs flex items-center justify-between">
                              <span className="text-slate-500 font-medium">Bengkung Awal:</span>
                              <span className="text-[11px] font-bold text-slate-800">
                                {getSilatRankFromBengkung(m.bengkung || 'Putih')}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Panel 3: Kediaman & Maklumat Waris */}
                        <div className="bg-slate-50/70 rounded-2xl p-3.5 border border-slate-200/70 space-y-2 flex flex-col justify-between">
                          <div className="space-y-2">
                            <div className="flex items-center gap-1.5 text-[11px] font-extrabold text-slate-500 uppercase tracking-wider border-b border-slate-200/60 pb-1.5">
                              <Home className="w-3.5 h-3.5 text-slate-400" />
                              <span>Kediaman & Penjaga</span>
                            </div>

                            {/* Alamat */}
                            {/* Alamat */}
                            {canViewMemberSensitiveInfo(currentUser, m) ? (
                              <div className="text-xs space-y-1">
                                <div className="flex items-center justify-between">
                                  <span className="text-slate-500 font-medium">Alamat:</span>
                                  {m.address && (
                                    <div className="flex items-center gap-1">
                                      <a
                                        href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(m.address)}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="p-1 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-md transition-colors inline-flex items-center gap-0.5 text-[10px] font-bold"
                                        title="Buka di Google Maps"
                                      >
                                        <Navigation className="w-3 h-3" />
                                        <span>Peta</span>
                                      </a>
                                      <button
                                        type="button"
                                        onClick={() => handleCopyText(m.id, 'address', m.address || '')}
                                        className="p-1 text-slate-400 hover:text-slate-700 hover:bg-slate-200 rounded-md transition-colors"
                                        title="Salin Alamat"
                                      >
                                        {copiedField?.id === m.id && copiedField?.type === 'address' ? (
                                          <Check className="w-3 h-3 text-emerald-600" />
                                        ) : (
                                          <Copy className="w-3 h-3" />
                                        )}
                                      </button>
                                    </div>
                                  )}
                                </div>
                                <p className="text-[11px] text-slate-700 font-medium bg-white p-2 rounded-xl border border-slate-200/80 leading-relaxed line-clamp-2">
                                  {m.address || 'Tiada alamat dinyatakan'}
                                </p>
                              </div>
                            ) : (
                              <div className="text-xs space-y-1">
                                <div className="flex items-center justify-between">
                                  <span className="text-slate-500 font-medium">Alamat:</span>
                                  <span className="text-[10px] font-semibold text-slate-400 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200">
                                    Peribadi
                                  </span>
                                </div>
                                <p className="text-[11px] text-slate-400 italic bg-white p-2 rounded-xl border border-dashed border-slate-200 leading-relaxed">
                                  Dilindungi (Akses Pengurusan sahaja)
                                </p>
                              </div>
                            )}

                            {/* Waris / Penjaga if any */}
                            {canViewMemberSensitiveInfo(currentUser, m) && (m.kinName || m.kinPhone) && (
                              <div className="pt-1.5 border-t border-slate-200/60 flex items-center justify-between text-xs">
                                <div className="truncate">
                                  <span className="text-slate-400 text-[10px] block font-medium">Waris / Penjaga:</span>
                                  <span className="font-bold text-slate-800 text-[11px] truncate block">{m.kinName || 'Waris'}</span>
                                </div>
                                {m.kinPhone && (
                                  <a
                                    href={`https://wa.me/6${m.kinPhone.replace(/\D/g, '')}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 hover:bg-emerald-100 text-emerald-700 rounded-lg text-[10px] font-bold border border-emerald-200"
                                    title="WhatsApp Waris"
                                  >
                                    <MessageCircle className="w-2.5 h-2.5" />
                                    <span>WhatsApp</span>
                                  </a>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Card Footer: Action Buttons */}
                      <div className="bg-slate-50/50 px-4 sm:px-6 py-3 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                        <button
                          type="button"
                          onClick={() => setPreviewPendingMember(m)}
                          className="inline-flex items-center gap-1.5 text-xs font-bold text-slate-600 hover:text-slate-900 bg-white hover:bg-slate-100 border border-slate-200 px-3 py-2 rounded-xl transition-all cursor-pointer shadow-2xs self-start sm:self-auto"
                        >
                          <Eye className="w-3.5 h-3.5 text-slate-500" />
                          <span>Lihat Butiran Penuh</span>
                        </button>

                        {/* Controls according to status tab */}
                        <div className="flex items-center gap-2 self-end sm:self-auto">
                          {pendingStatusTab === 'menunggu' && (
                            rejectingMemberId === m.id ? (
                              <div className="flex items-center gap-1.5 bg-rose-50 p-1.5 rounded-xl border border-rose-200 animate-fade-in">
                                <span className="text-xs font-bold text-rose-800 px-1">Pasti tolak?</span>
                                <button
                                  type="button"
                                  onClick={() => handleReject(m.id, m.name)}
                                  className="px-3 py-1.5 bg-rose-600 hover:bg-rose-700 text-white text-xs font-black rounded-lg cursor-pointer transition-colors border-none shadow-2xs"
                                >
                                  Sahkan Tolak
                                </button>
                                <button
                                  type="button"
                                  onClick={() => setRejectingMemberId(null)}
                                  className="px-2.5 py-1.5 bg-slate-200 text-slate-700 text-xs font-bold rounded-lg cursor-pointer transition-colors border-none"
                                >
                                  Batal
                                </button>
                              </div>
                            ) : (
                              <>
                                <button
                                  type="button"
                                  onClick={() => setRejectingMemberId(m.id)}
                                  className="inline-flex items-center gap-1 px-3 py-2 bg-white hover:bg-rose-50 text-rose-600 hover:text-rose-700 text-xs font-bold rounded-xl transition-colors border border-slate-200 hover:border-rose-200 cursor-pointer"
                                  title="Tolak Pendaftaran"
                                >
                                  <UserX className="w-3.5 h-3.5 shrink-0" />
                                  <span>Tolak</span>
                                </button>
                                <button
                                  type="button"
                                  onClick={() => handleApprove(m)}
                                  className="inline-flex items-center gap-1.5 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black rounded-xl transition-all shadow-sm cursor-pointer border-none active:scale-95"
                                >
                                  <CheckCircle2 className="w-4 h-4 shrink-0" />
                                  <span>Luluskan Keahlian</span>
                                </button>
                              </>
                            )
                          )}

                          {pendingStatusTab === 'diluluskan' && (
                            <div className="flex items-center gap-2">
                              <span className="text-xs font-bold text-emerald-800 bg-emerald-50 px-3 py-1.5 rounded-xl border border-emerald-200 flex items-center gap-1.5">
                                <ShieldCheck className="w-4 h-4 text-emerald-600" />
                                <span>Diluluskan oleh {m.approvedByName || 'Pengurusan'}</span>
                              </span>
                            </div>
                          )}

                          {pendingStatusTab === 'ditolak' && (
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-xs font-bold text-rose-800 bg-rose-50 px-3 py-1.5 rounded-xl border border-rose-200 flex items-center gap-1.5">
                                <UserX className="w-4 h-4 text-rose-600" />
                                <span>Ditolak oleh {m.rejectedByName || 'Pengurusan'}</span>
                              </span>
                              <button
                                type="button"
                                onClick={() => handleApprove(m)}
                                className="inline-flex items-center gap-1 px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black rounded-xl transition-all shadow-xs cursor-pointer border-none"
                                title="Luluskan semula permohonan yang ditolak"
                              >
                                <RotateCcw className="w-3.5 h-3.5" />
                                <span>Luluskan Semula</span>
                              </button>
                              {isSuperAdmin(currentUser) && (
                                <button
                                  type="button"
                                  onClick={() => handleDeleteMember(m.id, m.name)}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 hover:text-rose-800 text-xs font-bold rounded-xl transition-colors border border-rose-200 cursor-pointer shadow-2xs"
                                  title="Padam rekod permohonan ditolak (Superadmin sahaja)"
                                >
                                  <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                                  <span>Padam Rekod</span>
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              /* Empty State for Cards */
              <div className="bg-white rounded-3xl border border-slate-200 shadow-2xs p-10 sm:p-14 text-center space-y-4">
                <div className={`w-16 h-16 rounded-3xl flex items-center justify-center mx-auto border shadow-xs ${pendingStatusTab === 'ditolak'
                  ? 'bg-rose-50 text-rose-600 border-rose-200'
                  : pendingStatusTab === 'diluluskan'
                    ? 'bg-emerald-50 text-emerald-600 border-emerald-200'
                    : 'bg-emerald-50 text-emerald-600 border-emerald-200'
                  }`}>
                  {pendingStatusTab === 'ditolak' ? (
                    <UserX className="w-8 h-8" />
                  ) : (
                    <CheckCircle2 className="w-8 h-8" />
                  )}
                </div>
                <div className="space-y-1.5 max-w-md mx-auto">
                  <h4 className="text-base sm:text-lg font-black text-slate-900">
                    {pendingSearchTerm || pendingGelanggangFilter !== 'all'
                      ? 'Tiada Padanan Permohonan Ditemui'
                      : pendingStatusTab === 'diluluskan'
                        ? 'Tiada Rekod Ahli Telah Diluluskan'
                        : pendingStatusTab === 'ditolak'
                          ? 'Tiada Permohonan Ditolak'
                          : 'Semua Permohonan Telah Diluluskan'}
                  </h4>
                  <p className="text-xs text-slate-500 leading-relaxed">
                    {pendingSearchTerm || pendingGelanggangFilter !== 'all'
                      ? 'Tiada rekod yang sepadan dengan kriteria carian atau tapisan yang dipilih.'
                      : pendingStatusTab === 'diluluskan'
                        ? 'Belum ada ahli yang diluluskan melalui portal semakan ini.'
                        : pendingStatusTab === 'ditolak'
                          ? 'Tiada permohonan keahlian yang berstatus ditolak dalam rekod pangkalan data.'
                          : 'Tiada permohonan keahlian baharu yang belum disemak. Semua pesilat telah diluluskan dan diaktifkan.'}
                  </p>
                </div>
                <div className="flex items-center justify-center gap-2 pt-2">
                  {(pendingSearchTerm || pendingGelanggangFilter !== 'all') && (
                    <button
                      type="button"
                      onClick={() => {
                        setPendingSearchTerm('');
                        setPendingGelanggangFilter('all');
                      }}
                      className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl transition-colors cursor-pointer border-none"
                    >
                      Set Semula Tapisan
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleSubTabChange('direktori')}
                    className="px-4 py-2 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl transition-all shadow-xs cursor-pointer border-none"
                  >
                    Lihat Direktori Ahli Aktif →
                  </button>
                </div>
              </div>
            )
          )}
        </div>
      )}

      {/* SUBTAB 2: DIREKTORI AHLI (ACTIVE MEMBERS LIST) */}
      {activeSubTab === 'direktori' && (
        <div className="space-y-5 animate-fade-in">
          {/* Controls: Search, Role & Bengkung Filters */}
          <div className="flex flex-col space-y-3 bg-white p-4 rounded-2xl border border-slate-200 shadow-sm">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              {/* Search Input */}
              <div className="relative w-full sm:w-80">
                <Search className="absolute left-3.5 top-2.5 w-4 h-4 text-slate-400" />
                <input
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={isManagement ? "Cari nama atau No. IC ahli..." : "Cari nama pesilat..."}
                  className="w-full pl-10 pr-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 placeholder-slate-400 font-medium"
                />
              </div>

              <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar flex-nowrap w-full sm:w-auto pb-0.5">
                <span className="text-xs text-slate-400 font-medium mr-1 shrink-0">Peranan:</span>
                {roleFilters.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    onClick={() => handleRoleFilterChange(r.id)}
                    className={`px-3 py-1.5 rounded-lg text-xs font-bold whitespace-nowrap shrink-0 transition-all cursor-pointer ${roleFilter === r.id
                      ? 'bg-asmm-red text-white shadow-sm font-bold'
                      : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200'
                      }`}
                  >
                    {r.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Bengkung Filter Buttons & Sort Selector */}
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pt-2 border-t border-slate-100">
              <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar flex-nowrap pb-0.5">
                <span className="text-xs text-slate-400 font-medium mr-1 shrink-0">Bengkung:</span>
                {[
                  { id: 'all', label: 'Semua Bengkung' },
                  { id: 'Putih', label: 'Putih' },
                  { id: 'Hitam', label: 'Hitam' },
                  { id: 'Merah', label: 'Merah' },
                  { id: 'Kuning', label: 'Kuning' },
                ].map((b) => (
                  <button
                    key={b.id}
                    onClick={() => setBengkungFilter(b.id)}
                    className={`px-3 py-1 rounded-lg text-xs font-bold whitespace-nowrap shrink-0 transition-all ${bengkungFilter === b.id
                      ? 'bg-slate-900 text-white shadow-sm font-bold'
                      : 'bg-slate-50 text-slate-600 hover:bg-slate-100 border border-slate-200'
                      }`}
                  >
                    {b.label}
                  </button>
                ))}
              </div>

              {/* Sort by dropdown */}
              <div className="flex items-center gap-1.5 shrink-0 self-end sm:self-auto">
                <span className="text-xs text-slate-400 font-medium flex items-center gap-1">
                  <ArrowUpDown className="w-3.5 h-3.5 text-slate-400" />
                  <span>Susunan Ahli:</span>
                </span>
                <select
                  value={directorySortBy}
                  onChange={(e) => handleSortChange(e.target.value as DirectorySortOption)}
                  className="text-xs py-1.5 px-2.5 rounded-lg bg-slate-50 border border-slate-200 text-slate-700 font-semibold focus:outline-none focus:border-asmm-red cursor-pointer"
                  title="Susunan Ahli"
                >
                  <option value="default">Default</option>
                  <option value="id_asc">ID Ahli (Menaik)</option>
                  <option value="id_desc">ID Ahli (Menurun)</option>
                  <option value="age_desc">Umur (Tertua)</option>
                  <option value="age_asc">Umur (Termuda)</option>
                  <option value="bengkung_desc">Bengkung (Tertinggi)</option>
                  <option value="bengkung_asc">Bengkung (Terendah)</option>
                  <option value="name">Nama (A - Z)</option>
                  <option value="name_desc">Nama (Z - A)</option>
                  <option value="oldest">Tarikh Masuk (Terawal)</option>
                  <option value="newest">Tarikh Masuk (Terkini)</option>
                </select>
              </div>
            </div>
          </div>

          {/* Member Cards Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {paginatedMembers.map((member) => (
              <div
                key={member.id}
                onClick={() => setSelectedMember(member)}
                className="bg-white rounded-3xl border border-slate-200 shadow-sm hover:border-slate-300 transition-all flex flex-col justify-between group cursor-pointer overflow-hidden"
              >
                {/* Top Padded Info */}
                <div className="p-5 pb-3">
                  <div className="flex items-start gap-4">
                    <img
                      src={member.photoUrl || '/default-avatar.svg'}
                      alt={member.name}
                      onError={(e) => { (e.target as HTMLElement).setAttribute('src', '/default-avatar.svg'); }}
                      className="w-16 h-16 rounded-2xl object-cover border border-slate-200 shadow-sm shrink-0 group-hover:scale-105 transition-transform"
                    />

                    <div className="overflow-hidden flex-1 min-w-0">
                      <div className="flex flex-col items-start gap-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className={`text-[10px] px-2 py-0.5 rounded border uppercase tracking-wider ${getRoleBadgeStyle(member.role)}`}>
                            {member.role}
                          </span>
                        </div>
                        <span className="text-[10px] font-mono font-bold text-slate-600 bg-slate-100 px-1.5 py-0.5 rounded border border-slate-200">
                          {sanitizeMemberId(member.id)}
                        </span>
                      </div>
                      <h3 className="text-sm font-bold text-slate-900 mt-1.5 truncate group-hover:text-asmm-red transition-colors">
                        {member.name}
                      </h3>
                      {renderIcNo(member) && (
                        <div className="text-[11px] text-slate-500 font-mono flex items-center">
                          {renderIcNo(member)}
                          {calculateAge(member.icNo) > 0 && (
                            <span className="ml-1.5 font-sans font-medium text-slate-400">
                              • {calculateAge(member.icNo)} thn
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>

                  {member.role !== 'Waris' && (
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-slate-100">
                      <span className="text-[10px] font-extrabold text-slate-500 uppercase tracking-wider">Tahap & Pangkat:</span>
                      <span className="text-xs font-bold text-asmm-red">
                        {getSilatRankFromBengkung(member.bengkung)}
                      </span>
                    </div>
                  )}
                </div>

                {/* FULL CARD WIDTH EDGE-TO-EDGE BENGKUNG BELT BANNER (0px padding left/right, 100% width) */}
                {member.role !== 'Waris' ? (
                  <div className="w-full">
                    <BengkungCulaBadge bengkung={member.bengkung} size="md" fullWidth={true} />
                  </div>
                ) : (
                  <div className="border-t border-slate-100" />
                )}

                {/* Bottom Padded Info */}
                <div className="p-5 pt-3 text-xs space-y-1.5">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-500">Gelanggang:</span>
                    <span className="font-semibold text-slate-800">{member.gelanggang === 'Semua Gelanggang' ? 'ASMM' : member.gelanggang}</span>
                  </div>
                  <div className="flex items-center justify-between text-[11px] text-slate-500 pt-1.5 border-t border-slate-100">
                    <span className="flex items-center gap-1 text-slate-400">
                      <Calendar className="w-3 h-3" />
                      <span>Tarikh Masuk:</span>
                    </span>
                    <span className="font-semibold text-slate-700">{formatSubmissionDate(member.joinedDate)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between border-t border-slate-200 bg-white px-4 py-3 sm:px-6 rounded-3xl shadow-xs">
              <div className="flex flex-1 justify-between sm:hidden">
                <button
                  onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                >
                  Sebelumnya
                </button>
                <button
                  onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                  disabled={currentPage === totalPages}
                  className="relative ml-3 inline-flex items-center rounded-xl border border-slate-300 bg-white px-4 py-2 text-xs font-bold text-slate-700 hover:bg-slate-50 disabled:opacity-40"
                >
                  Seterusnya
                </button>
              </div>
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className="text-xs text-slate-700 font-medium">
                    Menunjukkan <span className="font-extrabold">{(currentPage - 1) * itemsPerPage + 1}</span> hingga{' '}
                    <span className="font-extrabold">
                      {Math.min(currentPage * itemsPerPage, sortedMembers.length)}
                    </span>{' '}
                    daripada <span className="font-extrabold">{sortedMembers.length}</span> ahli
                  </p>
                </div>
                <div>
                  <nav className="isolate inline-flex -space-x-px rounded-xl shadow-2xs gap-1" aria-label="Pagination">
                    <button
                      onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                      disabled={currentPage === 1}
                      className="relative inline-flex items-center rounded-l-xl px-2 py-2 text-slate-400 hover:bg-slate-50 focus:z-20 focus:outline-offset-0 disabled:opacity-40"
                    >
                      <span className="sr-only">Sebelumnya</span>
                      &larr;
                    </button>
                    {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((page) => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        aria-current={currentPage === page ? 'page' : undefined}
                        className={`relative inline-flex items-center px-4 py-2 text-xs font-bold rounded-xl transition-all ${currentPage === page
                          ? 'z-10 bg-asmm-red text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600'
                          : 'text-slate-900 hover:bg-slate-50 focus:outline-offset-0 border border-slate-200'
                          }`}
                      >
                        {page}
                      </button>
                    ))}
                    <button
                      onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                      disabled={currentPage === totalPages}
                      className="relative inline-flex items-center rounded-r-xl px-2 py-2 text-slate-400 hover:bg-slate-50 focus:z-20 focus:outline-offset-0 disabled:opacity-40"
                    >
                      <span className="sr-only">Seterusnya</span>
                      &rarr;
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* SUBTAB 3: DAFTAR PESILAT / WARIS BAHARU (DEDICATED FULL PAGE VIEW) */}
      {activeSubTab === 'daftar' && (
        !isExecutive ? (
          <div className="bg-white rounded-3xl border border-slate-200 p-8 sm:p-12 text-center space-y-4 max-w-lg mx-auto shadow-sm my-8">
            <div className="w-14 h-14 rounded-3xl bg-red-50 text-asmm-red flex items-center justify-center mx-auto shadow-2xs">
              <Lock className="w-7 h-7" />
            </div>
            <div className="space-y-1">
              <h3 className="text-base font-black text-slate-900">Akses Terhad Pentadbiran</h3>
              <p className="text-xs text-slate-500 leading-relaxed">
                Hanya pihak Pengurusan dan Superadmin dibenarkan mendaftar ahli atau waris baharu. Sila hubungi urusetia persatuan untuk maklumat lanjut.
              </p>
            </div>
            <button
              type="button"
              onClick={() => handleSubTabChange('direktori')}
              className="px-5 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-extrabold rounded-xl transition-all cursor-pointer border-none"
            >
              Kembali ke Direktori
            </button>
          </div>
        ) : (
          <div className="space-y-6 animate-fade-in pb-12">
            {/* Top Back Navigation Bar */}
            <div className="flex items-center justify-between">
              <button
                type="button"
                onClick={() => handleSubTabChange('direktori')}
                className="inline-flex items-center gap-2 text-xs font-extrabold text-slate-700 hover:text-asmm-red transition-all cursor-pointer bg-white px-4 py-2.5 rounded-2xl border border-slate-200 shadow-2xs hover:border-asmm-red/30 active:scale-95"
              >
                <ArrowLeft className="w-4 h-4 text-asmm-red" />
                <span>Kembali ke Direktori Ahli</span>
              </button>

              <span className="text-[11px] font-bold text-slate-500 bg-white px-3 py-1.5 rounded-xl border border-slate-200 shadow-2xs flex items-center gap-1.5">
                <Shield className="w-3.5 h-3.5 text-asmm-red" />
                <span>Borang Rasmi PSSASMM</span>
              </span>
            </div>

            {/* Registration Type Segmented Switcher */}
            <div className="bg-white rounded-3xl border border-slate-200/90 shadow-sm p-4 sm:p-5">
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                <div>
                  <h3 className="text-sm font-black text-slate-900">Kategori Pendaftaran Baharu</h3>
                  <p className="text-xs text-slate-500">Pilih jenis pendaftaran sama ada Pesilat (Ahli) atau Waris / Penjaga.</p>
                </div>
                <div className="flex items-center gap-1.5 p-1 bg-slate-100 rounded-2xl border border-slate-200/80 self-start sm:self-auto">
                  <button
                    type="button"
                    onClick={() => handleRegistrationTypeChange('pesilat')}
                    className={`px-4 py-2 rounded-xl text-xs font-black transition-all flex items-center gap-2 cursor-pointer border-none ${
                      registrationType === 'pesilat'
                        ? 'bg-asmm-red text-white shadow-2xs'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <UserPlus className="w-3.5 h-3.5" />
                    <span>Pesilat Baharu</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleRegistrationTypeChange('waris')}
                    className={`px-4 py-2 rounded-xl text-xs font-black transition-all flex items-center gap-2 cursor-pointer border-none ${
                      registrationType === 'waris'
                        ? 'bg-slate-900 text-white shadow-2xs'
                        : 'text-slate-600 hover:text-slate-900'
                    }`}
                  >
                    <Users className="w-3.5 h-3.5 text-amber-400" />
                    <span>Waris Baharu</span>
                  </button>
                </div>
              </div>
            </div>

            {/* ============================================================== */}
            {/* VIEW A: BORANG PENDAFTARAN WARIS BAHARU (NAMA, IC & EMEL)       */}
            {/* ============================================================== */}
            {registrationType === 'waris' ? (
              <form onSubmit={handleAddMember} className="space-y-6 w-full">
                {/* Section 1: Maklumat Asas Waris */}
                <div className="bg-white rounded-3xl border border-slate-200/90 shadow-sm p-6 sm:p-8 space-y-5">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-2xl bg-red-50 text-asmm-red flex items-center justify-center font-black shadow-2xs">
                        <User className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="text-base font-black text-slate-900">Maklumat Asas Waris / Penjaga</h3>
                        <p className="text-xs text-slate-500">Nama penuh, No. IC, dan alamat emel rasmi bagi akaun waris.</p>
                      </div>
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-wider text-asmm-red bg-red-50 px-2.5 py-1 rounded-full border border-red-200/60">
                      Wajib Diisi
                    </span>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1.5">Nama Penuh Waris / Penjaga *</label>
                      <input
                        type="text"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Contoh: Siti Aminah binti Kassim"
                        className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <label className="text-xs font-bold text-slate-700">No. Kad Pengenalan (IC) Waris *</label>
                          {newMemberIcDetails && (
                            <span className="text-[10px] font-extrabold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-md border border-blue-200">
                              {newMemberIcDetails.age} Thn • {newMemberIcDetails.gender}
                            </span>
                          )}
                        </div>
                        <input
                          type="text"
                          required
                          value={icNo}
                          onChange={(e) => setIcNo(e.target.value)}
                          placeholder="Contoh: 820512-02-5566"
                          className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium font-mono"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1.5">Alamat Emel Waris *</label>
                        <input
                          type="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Contoh: sitiaminah@gmail.com"
                          className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
                        />
                        <p className="text-[10px] text-slate-400 mt-1">Digunakan untuk log masuk portal waris dan pautan Google.</p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Section 2: Butiran Tambahan Waris (Pilihan) */}
                <div className="bg-white rounded-3xl border border-slate-200/90 shadow-sm p-6 sm:p-8 space-y-5">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center font-black shadow-2xs">
                        <MapPin className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="text-base font-black text-slate-900">Maklumat Perhubungan &amp; Gelanggang (Pilihan)</h3>
                        <p className="text-xs text-slate-500">Boleh dikosongkan dan dikemaskini oleh waris kemudian.</p>
                      </div>
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-wider text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full border border-slate-200">
                      Pilihan
                    </span>
                  </div>

                  <div className="space-y-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1.5">No. Telefon Waris (Pilihan)</label>
                        <input
                          type="text"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          placeholder="Contoh: 019-4920194"
                          className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium font-mono"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1.5">Gelanggang Pilihan (Pilihan)</label>
                        <select
                          value={gelanggang === 'Semua Gelanggang' ? 'Pusaka Warisan' : gelanggang}
                          onChange={(e) => setGelanggang(e.target.value as Gelanggang)}
                          className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-bold"
                        >
                          <option value="Pusaka Warisan">Pusaka Warisan</option>
                          <option value="Wadi AnNour">Wadi AnNour</option>
                          <option value="Paya Besar">Paya Besar</option>
                          <option value="ASMM">ASMM (Pusat / Terbuka)</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1.5">Alamat Kediaman (Pilihan)</label>
                      <textarea
                        rows={2}
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        placeholder="Contoh: No 12, Jalan Sejahtera 3, Taman Sejahtera, 09600 Lunas, Kedah (Boleh dikemaskini kemudian)"
                        className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium resize-none leading-relaxed"
                      />
                    </div>
                  </div>
                </div>

                {/* Section 3: Pautan Pesilat / Tanggungan Sedia Ada (Pilihan) */}
                <div className="bg-white rounded-3xl border border-slate-200/90 shadow-sm p-6 sm:p-8 space-y-5">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center font-black shadow-2xs">
                        <HeartHandshake className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="text-base font-black text-slate-900">Pautan Pesilat Tanggungan (Pilihan)</h3>
                        <p className="text-xs text-slate-500">Pautkan waris ini terus kepada anak atau pesilat sedia ada jika telah diketahui.</p>
                      </div>
                    </div>

                    <div className="flex items-center gap-2 p-1 bg-slate-100 rounded-xl border border-slate-200 self-start sm:self-auto">
                      <button
                        type="button"
                        onClick={() => {
                          setIsWarisLinkPesilat(false);
                          setSelectedWarisChildMemberId('');
                          setWarisChildSearchTerm('');
                        }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer border-none ${
                          !isWarisLinkPesilat
                            ? 'bg-white text-slate-900 shadow-2xs'
                            : 'text-slate-500 hover:text-slate-800'
                        }`}
                      >
                        Daftar Waris Sahaja
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIsWarisLinkPesilat(true);
                        }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-extrabold transition-all cursor-pointer border-none flex items-center gap-1.5 ${
                          isWarisLinkPesilat
                            ? 'bg-blue-600 text-white shadow-2xs'
                            : 'text-slate-500 hover:text-slate-800'
                        }`}
                      >
                        <HeartHandshake className="w-3.5 h-3.5" />
                        <span>Pautkan Pesilat</span>
                      </button>
                    </div>
                  </div>

                  {isWarisLinkPesilat ? (
                    <div className="space-y-4 bg-blue-50/30 border border-blue-200/80 rounded-2xl p-4 sm:p-5">
                      <div className="space-y-1">
                        <label className="block text-xs font-black text-slate-800">
                          Pilih Pesilat Sedia Ada untuk Dipautkan (Pilihan)
                        </label>
                        <p className="text-[11px] text-slate-500 leading-relaxed">
                          Pilih pesilat yang berada di bawah jagaan waris ini. Pesilat akan dipautkan secara automatik ke rekod waris baharu ini.
                        </p>
                      </div>

                      {selectedWarisChildMember ? (
                        <div className="bg-white rounded-2xl border border-blue-300/80 p-4 shadow-2xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div className="flex items-center gap-3.5">
                            <img
                              src={selectedWarisChildMember.photoUrl || '/default-avatar.svg'}
                              alt={selectedWarisChildMember.name}
                              onError={(e) => { (e.target as HTMLElement).setAttribute('src', '/default-avatar.svg'); }}
                              className="w-12 h-12 rounded-2xl object-cover border border-slate-200 shrink-0"
                            />
                            <div className="space-y-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h4 className="text-sm font-black text-slate-900">{selectedWarisChildMember.name}</h4>
                                <span className="text-[10px] font-mono font-bold bg-slate-100 text-slate-700 px-2 py-0.5 rounded border border-slate-200">
                                  {sanitizeMemberId(selectedWarisChildMember.id)}
                                </span>
                                <span className="text-[10px] font-extrabold bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-md">
                                  {selectedWarisChildMember.gelanggang}
                                </span>
                              </div>
                              <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                                <span className="font-mono">{selectedWarisChildMember.icNo}</span>
                                {selectedWarisChildMember.bengkung && (
                                  <>
                                    <span>•</span>
                                    <span className="font-semibold text-slate-700">{selectedWarisChildMember.bengkung}</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 self-start sm:self-center shrink-0">
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedWarisChildMemberId('');
                                setWarisChildSearchTerm('');
                              }}
                              className="px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs font-bold rounded-xl transition-all cursor-pointer border border-rose-200"
                            >
                              Tukar Pesilat
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="relative">
                            <Search className="absolute left-3.5 top-3 w-4 h-4 text-slate-400" />
                            <input
                              type="text"
                              value={warisChildSearchTerm}
                              onChange={(e) => setWarisChildSearchTerm(e.target.value)}
                              placeholder="Cari nama pesilat, No. IC, ID pesilat, atau gelanggang..."
                              className="w-full pl-10 pr-10 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-blue-500 focus:outline-none text-xs text-slate-900 font-medium"
                            />
                            {warisChildSearchTerm && (
                              <button
                                type="button"
                                onClick={() => setWarisChildSearchTerm('')}
                                className="absolute right-2.5 top-2.5 p-1 text-slate-400 hover:text-slate-600 rounded-lg transition-colors cursor-pointer border-none bg-transparent"
                                title="Padam carian"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>

                          <div className="max-h-56 overflow-y-auto space-y-1.5 bg-white rounded-2xl p-2 border border-slate-200 shadow-2xs">
                            {filteredWarisChildren.length > 0 ? (
                              filteredWarisChildren.map((m) => (
                                <button
                                  key={m.id}
                                  type="button"
                                  onClick={() => {
                                    setSelectedWarisChildMemberId(m.id);
                                    if (!address && m.address) setAddress(m.address);
                                    if (m.gelanggang && m.gelanggang !== 'Semua Gelanggang' && m.gelanggang !== 'Tiada') {
                                      setGelanggang(m.gelanggang as Gelanggang);
                                    }
                                  }}
                                  className="w-full p-2.5 rounded-xl text-left hover:bg-blue-50/60 transition-all flex items-center justify-between gap-3 cursor-pointer border border-transparent hover:border-blue-200 group"
                                >
                                  <div className="flex items-center gap-2.5 min-w-0">
                                    <img
                                      src={m.photoUrl || '/default-avatar.svg'}
                                      alt={m.name}
                                      onError={(e) => { (e.target as HTMLElement).setAttribute('src', '/default-avatar.svg'); }}
                                      className="w-8 h-8 rounded-xl object-cover border border-slate-200 shrink-0"
                                    />
                                    <div className="min-w-0">
                                      <div className="flex items-center gap-1.5">
                                        <span className="text-xs font-black text-slate-900 truncate group-hover:text-blue-700">
                                          {m.name}
                                        </span>
                                        <span className="text-[10px] font-mono font-bold text-slate-600 bg-slate-100 px-1.5 py-0.2 rounded border border-slate-200 shrink-0">
                                          {sanitizeMemberId(m.id)}
                                        </span>
                                      </div>
                                      <span className="text-[11px] text-slate-500 block truncate font-mono">
                                        {m.icNo} • {m.gelanggang}
                                      </span>
                                    </div>
                                  </div>
                                  <span className="text-[11px] font-extrabold text-blue-600 group-hover:underline shrink-0">
                                    Pilih Pesilat &rarr;
                                  </span>
                                </button>
                              ))
                            ) : (
                              <div className="p-4 text-center text-xs text-slate-500">
                                Tiada padanan pesilat ditemui untuk "{warisChildSearchTerm}".
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Hubungan Waris dengan Pesilat */}
                      <div className="pt-2 border-t border-blue-200/60">
                        <label className="block text-xs font-bold text-slate-800 mb-1.5">
                          Hubungan Waris dengan Pesilat
                        </label>
                        <select
                          value={warisChildRelationship}
                          onChange={(e) => setWarisChildRelationship(e.target.value)}
                          className="w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-blue-500 focus:outline-none text-xs text-slate-900 font-bold"
                        >
                          <option value="Ibu">Ibu (Waris adalah Ibu)</option>
                          <option value="Bapa">Bapa (Waris adalah Bapa)</option>
                          <option value="Penjaga / Waris">Penjaga / Waris Sah</option>
                          <option value="Isteri">Isteri</option>
                          <option value="Suami">Suami</option>
                          <option value="Abang / Kakak">Abang / Kakak</option>
                          <option value="Lain-lain">Lain-lain Hubungan Keluarga</option>
                        </select>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200/80 text-xs text-slate-600 flex items-center justify-between">
                      <span>Waris akan didaftarkan sebagai akaun bebas (pautan anak boleh dilakukan kemudian).</span>
                      <button
                        type="button"
                        onClick={() => setIsWarisLinkPesilat(true)}
                        className="text-blue-600 font-extrabold hover:underline ml-2 shrink-0 cursor-pointer"
                      >
                        Pautkan Pesilat &rarr;
                      </button>
                    </div>
                  )}
                </div>

                {/* Form Actions Footer */}
                <div className="bg-white rounded-3xl border border-slate-200/90 shadow-sm p-4 sm:p-6 flex flex-col sm:flex-row items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => handleSubTabChange('direktori')}
                    className="w-full sm:w-auto px-5 py-2.5 text-xs font-bold text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded-xl transition-all cursor-pointer border-none"
                  >
                    Batal / Kembali
                  </button>

                  <button
                    type="submit"
                    className="w-full sm:w-auto px-6 py-2.5 bg-slate-900 hover:bg-slate-800 text-white font-black text-xs rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer shadow-sm active:scale-95 border-none"
                  >
                    <CheckCircle2 className="w-4 h-4 text-amber-400" />
                    <span>Daftar Waris Baharu</span>
                  </button>
                </div>
              </form>
            ) : (
              /* ============================================================== */
              /* VIEW B: BORANG PENDAFTARAN PESILAT BAHARU (ALIRAN LENGKAP)      */
              /* ============================================================== */
              <form onSubmit={handleAddMember} className="space-y-6 w-full">
                {/* Section 1: Maklumat Peribadi Pesilat */}
                <div className="bg-white rounded-3xl border border-slate-200/90 shadow-sm p-6 sm:p-8 space-y-5">
                  <div className="flex items-center justify-between border-b border-slate-100 pb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-2xl bg-red-50 text-asmm-red flex items-center justify-center font-black shadow-2xs">
                        <User className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="text-base font-black text-slate-900">Maklumat Peribadi Pesilat</h3>
                        <p className="text-xs text-slate-500">Nama penuh dan pengenalan diri rasmi pesilat.</p>
                      </div>
                    </div>
                    <span className="text-[10px] font-black uppercase tracking-wider text-asmm-red bg-red-50 px-2.5 py-1 rounded-full border border-red-200/60">
                      Wajib Diisi
                    </span>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1.5">Nama Penuh Pesilat *</label>
                      <input
                        type="text"
                        required
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder="Contoh: Muhammad Amirul Bin Ridzuan"
                        className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
                      />
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div>
                        <div className="flex items-center justify-between mb-1.5">
                          <label className="text-xs font-bold text-slate-700">No. Kad Pengenalan (IC) *</label>
                          {newMemberIcDetails && (
                            <span className="text-[10px] font-extrabold text-blue-700 bg-blue-50 px-2 py-0.5 rounded-md border border-blue-200">
                              {newMemberIcDetails.age} Thn • {newMemberIcDetails.gender} • {newMemberIcDetails.category}
                            </span>
                          )}
                        </div>
                        <input
                          type="text"
                          required
                          value={icNo}
                          onChange={(e) => setIcNo(e.target.value)}
                          placeholder="Contoh: 030819-02-5819"
                          className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium font-mono"
                        />
                      </div>

                      <div>
                        <label className="block text-xs font-bold text-slate-700 mb-1.5">No. Telefon (Pilihan)</label>
                        <input
                          type="text"
                          value={phone}
                          onChange={(e) => setPhone(e.target.value)}
                          placeholder="Contoh: 019-4920194"
                          className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium font-mono"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1.5">Alamat Emel (Pilihan)</label>
                      <input
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        placeholder="Contoh: pesilat@gmail.com (Kosongkan jika tiada)"
                        className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium"
                      />
                      <p className="text-[10px] text-slate-400 mt-1">Pilihan. Jika dibiarkan kosong, ahli atau waris boleh mengisi dan mengemaskini emel kemudian.</p>
                    </div>

                    <div>
                      <div className="flex items-center justify-between mb-1.5">
                        <label className="text-xs font-bold text-slate-700">Alamat Kediaman (Pilihan)</label>
                        <span className="text-[10px] text-slate-400">Boleh diisi sendiri oleh pesilat kemudian</span>
                      </div>
                      <textarea
                        rows={2}
                        value={address}
                        onChange={(e) => setAddress(e.target.value)}
                        placeholder="Contoh: No 12, Jalan Sejahtera 3, Taman Sejahtera, 09600 Lunas, Kedah (Boleh dikemaskini kemudian)"
                        className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-medium resize-none leading-relaxed"
                      />
                    </div>
                  </div>
                </div>

                {/* Section 2: Pautan Unit Keluarga (Keluarga & Waris) */}
                <div className="bg-white rounded-3xl border border-slate-200/90 shadow-sm p-6 sm:p-8 space-y-5">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 border-b border-slate-100 pb-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-2xl bg-blue-50 text-blue-600 flex items-center justify-center font-black shadow-2xs">
                        <HeartHandshake className="w-5 h-5" />
                      </div>
                      <div>
                        <h3 className="text-base font-black text-slate-900">Pautan Unit Keluarga (Keluarga &amp; Waris)</h3>
                        <p className="text-xs text-slate-500">Hubungkan pesilat ini kepada profil ahli atau waris sedia ada dalam sistem ASMM.</p>
                      </div>
                    </div>

                    {/* Toggle Pill */}
                    <div className="flex items-center gap-2 p-1 bg-slate-100 rounded-xl border border-slate-200 self-start sm:self-auto">
                      <button
                        type="button"
                        onClick={() => {
                          setIsFamilyAttached(false);
                          setSelectedParentMemberId('');
                          setParentSearchTerm('');
                        }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all cursor-pointer border-none ${!isFamilyAttached
                          ? 'bg-white text-slate-900 shadow-2xs'
                          : 'text-slate-500 hover:text-slate-800'
                          }`}
                      >
                        Individu Bebas
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setIsFamilyAttached(true);
                          setSelectedParentMemberId('');
                          setParentSearchTerm('');
                        }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-extrabold transition-all cursor-pointer border-none flex items-center gap-1.5 ${isFamilyAttached
                          ? 'bg-blue-600 text-white shadow-2xs'
                          : 'text-slate-500 hover:text-slate-800'
                          }`}
                      >
                        <HeartHandshake className="w-3.5 h-3.5" />
                        <span>Pautkan ke Keluarga</span>
                      </button>
                    </div>
                  </div>

                  {isFamilyAttached ? (
                    <div className="space-y-4 bg-blue-50/30 border border-blue-200/80 rounded-2xl p-4 sm:p-5">
                      <div className="space-y-1">
                        <label className="block text-xs font-black text-slate-800">
                          Pilih Ahli Keluarga / Waris Sedia Ada *
                        </label>
                        <p className="text-[11px] text-slate-500 leading-relaxed">
                          Pilih ketua keluarga atau waris yang memegang rekod pesilat ini. Pesilat akan muncul dalam halaman 'Keluarga' waris tersebut.
                        </p>
                      </div>

                      {selectedParentMember ? (
                        <div className="bg-white rounded-2xl border border-blue-300/80 p-4 shadow-2xs flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                          <div className="flex items-center gap-3.5">
                            <img
                              src={selectedParentMember.photoUrl || '/default-avatar.svg'}
                              alt={selectedParentMember.name}
                              onError={(e) => { (e.target as HTMLElement).setAttribute('src', '/default-avatar.svg'); }}
                              className="w-12 h-12 rounded-2xl object-cover border border-slate-200 shrink-0"
                            />
                            <div className="space-y-1">
                              <div className="flex items-center gap-2 flex-wrap">
                                <h4 className="text-sm font-black text-slate-900">{selectedParentMember.name}</h4>
                                <span className="text-[10px] font-mono font-bold bg-slate-100 text-slate-700 px-2 py-0.5 rounded border border-slate-200">
                                  {sanitizeMemberId(selectedParentMember.id)}
                                </span>
                                <span className="text-[10px] font-extrabold bg-blue-50 text-blue-700 border border-blue-200 px-2 py-0.5 rounded-md">
                                  {selectedParentMember.role || 'Ahli'}
                                </span>
                              </div>
                              <div className="flex items-center gap-3 text-xs text-slate-500 flex-wrap">
                                <span className="font-mono">{selectedParentMember.icNo}</span>
                                <span>•</span>
                                <span className="font-medium">{selectedParentMember.gelanggang}</span>
                                {selectedParentMember.phone && (
                                  <>
                                    <span>•</span>
                                    <span className="font-mono">{selectedParentMember.phone}</span>
                                  </>
                                )}
                              </div>
                            </div>
                          </div>

                          <div className="flex items-center gap-2 self-start sm:self-center shrink-0">
                            {selectedParentMember.address && (
                              <button
                                type="button"
                                onClick={() => {
                                  setAddress(selectedParentMember.address || '');
                                  showToast('info', 'Alamat Disalin', 'Alamat kediaman waris telah disalin ke borang.');
                                }}
                                className="px-3 py-1.5 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-bold rounded-xl transition-all cursor-pointer border border-slate-200"
                                title="Salin alamat kediaman waris ini"
                              >
                                Salin Alamat Waris
                              </button>
                            )}
                            <button
                              type="button"
                              onClick={() => {
                                setSelectedParentMemberId('');
                                setParentSearchTerm('');
                              }}
                              className="px-3 py-1.5 bg-rose-50 hover:bg-rose-100 text-rose-700 text-xs font-bold rounded-xl transition-all cursor-pointer border border-rose-200"
                            >
                              Tukar Ahli
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-3">
                          <div className="relative">
                            <Search className="absolute left-3.5 top-3 w-4 h-4 text-slate-400" />
                            <input
                              autoFocus
                              type="text"
                              value={parentSearchTerm}
                              onChange={(e) => setParentSearchTerm(e.target.value)}
                              placeholder="Cari nama, No. IC, ID pesilat, telefon, atau gelanggang..."
                              className="w-full pl-10 pr-10 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-blue-500 focus:outline-none text-xs text-slate-900 font-medium"
                            />
                            {parentSearchTerm && (
                              <button
                                type="button"
                                onClick={() => setParentSearchTerm('')}
                                className="absolute right-2.5 top-2.5 p-1 text-slate-400 hover:text-slate-600 rounded-lg transition-colors cursor-pointer border-none bg-transparent"
                                title="Padam carian"
                              >
                                <X className="w-4 h-4" />
                              </button>
                            )}
                          </div>

                          {parentSearchTerm.trim() && (
                            <div className="flex items-center justify-between px-1 text-[11px] text-slate-500 font-bold">
                              <span>Hasil Carian Fuzzy:</span>
                              <span className="text-blue-600 bg-blue-50 px-2 py-0.5 rounded-full border border-blue-200 font-extrabold">
                                {filteredFamilyHeads.length} padanan
                              </span>
                            </div>
                          )}

                          <div className="max-h-60 overflow-y-auto space-y-1.5 bg-white rounded-2xl p-2 border border-slate-200 shadow-2xs">
                            {filteredFamilyHeads.length > 0 ? (
                              filteredFamilyHeads.map((m) => (
                                <button
                                  key={m.id}
                                  type="button"
                                  onClick={() => {
                                    setSelectedParentMemberId(m.id);
                                    if (!address && m.address) {
                                      setAddress(m.address);
                                    }
                                    if (m.gelanggang && m.gelanggang !== 'Semua Gelanggang' && m.gelanggang !== 'Tiada') {
                                      setGelanggang(m.gelanggang as Gelanggang);
                                    }
                                  }}
                                  className="w-full p-2.5 rounded-xl text-left hover:bg-blue-50/60 transition-all flex items-center justify-between gap-3 cursor-pointer border border-transparent hover:border-blue-200 group"
                                >
                                  <div className="flex items-center gap-2.5 min-w-0">
                                    <img
                                      src={m.photoUrl || '/default-avatar.svg'}
                                      alt={m.name}
                                      onError={(e) => { (e.target as HTMLElement).setAttribute('src', '/default-avatar.svg'); }}
                                      className="w-8 h-8 rounded-xl object-cover border border-slate-200 shrink-0"
                                    />
                                    <div className="min-w-0">
                                      <div className="flex items-center gap-1.5">
                                        <span className="text-xs font-black text-slate-900 truncate group-hover:text-blue-700">
                                          {m.name}
                                        </span>
                                        <span className="text-[10px] font-mono font-bold text-slate-600 bg-slate-100 px-1.5 py-0.2 rounded border border-slate-200 shrink-0">
                                          {sanitizeMemberId(m.id)}
                                        </span>
                                      </div>
                                      <span className="text-[11px] text-slate-500 block truncate font-mono">
                                        {m.icNo} • {m.gelanggang} ({m.role || 'Ahli'})
                                      </span>
                                    </div>
                                  </div>
                                  <span className="text-[11px] font-extrabold text-blue-600 group-hover:underline shrink-0">
                                    Pilih Ahli &rarr;
                                  </span>
                                </button>
                              ))
                            ) : (
                              <div className="p-5 text-center text-xs text-slate-500 space-y-1">
                                <p className="font-bold text-slate-700">Tiada padanan ahli ditemui untuk "{parentSearchTerm}"</p>
                                <p className="text-[11px] text-slate-400">Cuba taip sebahagian nama, No. IC tanpa sengkang, atau No. ID pesilat.</p>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Hubungan Keluarga Dropdown */}
                      <div className="pt-2 border-t border-blue-200/60">
                        <label className="block text-xs font-bold text-slate-800 mb-1.5">
                          Hubungan Keluarga dengan Ahli Terpilih (Dropdown Menu) *
                        </label>
                        <select
                          value={familyRelationship}
                          onChange={(e) => setFamilyRelationship(e.target.value)}
                          className="w-full px-4 py-2.5 rounded-xl bg-white border border-slate-200 focus:border-blue-500 focus:outline-none text-xs text-slate-900 font-bold"
                        >
                          <option value="Anak">Anak (Pesilat adalah anak kepada ahli ini)</option>
                          <option value="Isteri">Isteri (Pesilat adalah isteri)</option>
                          <option value="Suami">Suami (Pesilat adalah suami)</option>
                          <option value="Bapa">Bapa (Pesilat adalah bapa)</option>
                          <option value="Ibu">Ibu (Pesilat adalah ibu)</option>
                          <option value="Adik-beradik">Adik-beradik (Pesilat adalah saudara kandung)</option>
                          <option value="Bapa Saudara">Bapa Saudara (Pesilat adalah bapa saudara)</option>
                          <option value="Ibu Saudara">Ibu Saudara (Pesilat adalah ibu saudara)</option>
                          <option value="Penjaga / Waris">Penjaga / Waris (Di bawah jagaan waris ini)</option>
                          <option value="Lain-lain">Lain-lain Hubungan Keluarga</option>
                        </select>
                        <p className="text-[10px] text-slate-500 mt-1">
                          Pesilat akan dipaparkan secara rasmi dalam menu 'Keluarga' ahli berkenaan dan semakan ujian bengkung keluarga.
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="p-4 bg-slate-50 rounded-2xl border border-slate-200/80 text-xs text-slate-600 flex items-center justify-between">
                      <span>Pesilat didaftarkan sebagai ahli individu (tanpa pautan keluarga).</span>
                      <button
                        type="button"
                        onClick={() => setIsFamilyAttached(true)}
                        className="text-blue-600 font-extrabold hover:underline ml-2 shrink-0 cursor-pointer"
                      >
                        Pautkan Sekarang &rarr;
                      </button>
                    </div>
                  )}
                </div>

                {/* Section 3: Gelanggang, Bengkung & Peranan */}
                <div className="bg-white rounded-3xl border border-slate-200/90 shadow-sm p-6 sm:p-8 space-y-5">
                  <div className="flex items-center gap-3 border-b border-slate-100 pb-4">
                    <div className="w-10 h-10 rounded-2xl bg-amber-50 text-amber-600 flex items-center justify-center font-black shadow-2xs">
                      <Award className="w-5 h-5" />
                    </div>
                    <div>
                      <h3 className="text-base font-black text-slate-900">Gelanggang Latihan, Bengkung &amp; Peranan</h3>
                      <p className="text-xs text-slate-500">Tentukan gelanggang latihan pesilat, peringkat bengkung, dan peranan dalam pertubuhan.</p>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1.5">Gelanggang Latihan *</label>
                      <select
                        value={gelanggang === 'Semua Gelanggang' ? 'ASMM' : gelanggang}
                        onChange={(e) => setGelanggang(e.target.value as Gelanggang)}
                        className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-bold"
                      >
                        <option value="Pusaka Warisan">Pusaka Warisan</option>
                        <option value="Wadi AnNour">Wadi AnNour</option>
                        <option value="Paya Besar">Paya Besar</option>
                        <option value="ASMM">ASMM (Pusat / Terbuka)</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-xs font-bold text-slate-700 mb-1.5">Peringkat Bengkung Permulaan</label>
                      <select
                        value={getResolvedBengkungValue(bengkung, bengkungRanksList)}
                        onChange={(e) => setBengkung(e.target.value as BengkungLevel)}
                        className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-bold"
                      >
                        {bengkungRanksList.map((rank) => {
                          const optVal = getFormattedBengkungOption(rank);
                          return (
                            <option key={rank.code} value={optVal}>
                              {optVal}
                            </option>
                          );
                        })}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-slate-700 mb-1.5">Peranan Governance / Organisasi</label>
                    <select
                      value={role}
                      onChange={(e) => {
                        const val = e.target.value as Role;
                        if (val === 'Waris') {
                          handleRegistrationTypeChange('waris');
                        } else {
                          setRole(val);
                        }
                      }}
                      className="w-full px-4 py-2.5 rounded-xl bg-slate-50 border border-slate-200 focus:border-asmm-red focus:bg-white focus:outline-none text-xs text-slate-900 font-bold"
                    >
                      <option value="Ahli">Ahli Biasa (Pesilat)</option>
                      <option value="Waris">Waris / Penjaga (Borang Pantas Emel, Nama, IC)</option>
                      <option value="AJK">AJK (Ahli Jawatankuasa)</option>
                      <option value="Ketua Gelanggang">Ketua Gelanggang</option>
                      <option value="Setiausaha">Setiausaha</option>
                      <option value="Pen. Setiausaha">Penolong Setiausaha</option>
                      <option value="Bendahari">Bendahari</option>
                      <option value="Timbalan Pengerusi">Timbalan Pengerusi</option>
                      <option value="Pengerusi">Pengerusi</option>
                    </select>
                  </div>

                  {/* Onboarding Category Selection */}
                  {role === 'Ahli' && (
                    <div className="p-4 bg-slate-50 border border-slate-200 rounded-2xl space-y-2.5">
                      <label className="block text-xs font-black text-slate-800">
                        Kategori Onboarding &amp; Bayaran Permulaan
                      </label>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <button
                          type="button"
                          onClick={() => setIsOldMemberManual(true)}
                          className={`p-3.5 rounded-xl border text-left transition-all cursor-pointer ${isOldMemberManual
                            ? 'bg-amber-50 border-amber-300 text-amber-950 shadow-2xs'
                            : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                            }`}
                        >
                          <div className="flex items-center gap-1.5 font-black text-xs">
                            <CheckCircle2 className={`w-4 h-4 ${isOldMemberManual ? 'text-amber-600' : 'text-slate-400'}`} />
                            <span>Ahli Lama / Sedia Ada</span>
                          </div>
                          <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                            Selesai onboarding sebelum sistem wujud (Dikecualikan bayaran uniform RM130 & pendaftaran).
                          </p>
                        </button>

                        <button
                          type="button"
                          onClick={() => setIsOldMemberManual(false)}
                          className={`p-3.5 rounded-xl border text-left transition-all cursor-pointer ${!isOldMemberManual
                            ? 'bg-red-50 border-red-300 text-red-950 shadow-2xs'
                            : 'bg-white border-slate-200 text-slate-600 hover:bg-slate-50'
                            }`}
                        >
                          <div className="flex items-center gap-1.5 font-black text-xs">
                            <UserPlus className={`w-4 h-4 ${!isOldMemberManual ? 'text-asmm-red' : 'text-slate-400'}`} />
                            <span>Ahli Baharu (Onboarding Penuh)</span>
                          </div>
                          <p className="text-[11px] text-slate-500 mt-1 leading-snug">
                            Jana invois onboarding sistem (Uniform RM130 + Yuran Tahunan RM10/RM20 + Yuran Bulanan).
                          </p>
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Form Actions Footer */}
                <div className="bg-white rounded-3xl border border-slate-200/90 shadow-sm p-4 sm:p-6 flex flex-col sm:flex-row items-center justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => handleSubTabChange('direktori')}
                    className="w-full sm:w-auto px-5 py-2.5 text-xs font-bold text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded-xl transition-all cursor-pointer border-none"
                  >
                    Batal / Kembali
                  </button>

                  <button
                    type="submit"
                    className="w-full sm:w-auto px-6 py-2.5 bg-asmm-red hover:bg-asmm-crimson text-white font-black text-xs rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer shadow-sm active:scale-95 border-none"
                  >
                    <CheckCircle2 className="w-4 h-4" />
                    <span>Daftar Pesilat Baharu</span>
                  </button>
                </div>
              </form>
            )}
          </div>
        )
      )}

      {/* MEMBER DETAILS MODAL */}
      {selectedMember && (
        <Modal
          isOpen={!!selectedMember}
          onClose={() => setSelectedMember(null)}
          title={selectedMember.role === 'Waris' ? "Profil Butiran Waris" : "Profil Butiran Pesilat"}
        >
          <div className="space-y-5">
            {canSuperadminChangePhoto && (
              <input
                ref={memberPhotoInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleSuperadminMemberPhotoUpload}
              />
            )}

            <div className="flex items-start sm:items-center gap-4 bg-slate-50 p-4 rounded-2xl border border-slate-200">
              <div className="relative group shrink-0">
                <img
                  src={selectedMember.photoUrl || '/default-avatar.svg'}
                  alt={selectedMember.name}
                  onError={(e) => { (e.target as HTMLElement).setAttribute('src', '/default-avatar.svg'); }}
                  className={`w-20 h-20 rounded-2xl object-cover border border-slate-300 shadow-sm transition-all ${
                    canSuperadminChangePhoto
                      ? 'cursor-pointer group-hover:brightness-90 ring-2 ring-transparent group-hover:ring-red-500'
                      : ''
                  }`}
                  onClick={() => {
                    if (canSuperadminChangePhoto && !isUploadingMemberPhoto) {
                      memberPhotoInputRef.current?.click();
                    }
                  }}
                  title={canSuperadminChangePhoto ? "Klik untuk tukar foto profil (Superadmin)" : undefined}
                />

                {canSuperadminChangePhoto && (
                  <button
                    type="button"
                    onClick={() => memberPhotoInputRef.current?.click()}
                    disabled={isUploadingMemberPhoto}
                    className="absolute inset-0 rounded-2xl bg-black/45 opacity-0 group-hover:opacity-100 transition-opacity flex flex-col items-center justify-center text-white cursor-pointer shadow-inner border-0"
                    title="Muat naik foto profil baharu"
                  >
                    {isUploadingMemberPhoto ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      <>
                        <Camera className="w-5 h-5 drop-shadow-xs" />
                        <span className="text-[9px] font-black mt-0.5 drop-shadow-xs">Tukar Foto</span>
                      </>
                    )}
                  </button>
                )}

                {canSuperadminChangePhoto && isUploadingMemberPhoto && (
                  <div className="absolute inset-0 rounded-2xl bg-black/60 flex flex-col items-center justify-center text-white z-10">
                    <Loader2 className="w-5 h-5 animate-spin text-white" />
                    <span className="text-[9px] font-bold mt-1">Memuat naik...</span>
                  </div>
                )}
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] px-2 py-0.5 rounded border uppercase tracking-wider ${getRoleBadgeStyle(selectedMember.role)}`}>
                    {selectedMember.role}
                  </span>
                  {canSuperadminChangePhoto && (
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded bg-red-100 text-red-700 border border-red-200">
                      Superadmin Akses
                    </span>
                  )}
                </div>

                <h3 className="text-base font-extrabold text-slate-900 mt-1 truncate">{selectedMember.name}</h3>

                <div className="text-xs text-slate-500 font-mono mt-1 flex items-center gap-1.5 flex-wrap">
                  <span className="font-bold text-slate-800 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                    ID: {sanitizeMemberId(selectedMember.id)}
                  </span>
                  {renderIcNo(selectedMember) && (
                    <span>• IC: {renderIcNo(selectedMember)}</span>
                  )}
                </div>

                {/* Superadmin photo action buttons */}
                {canSuperadminChangePhoto && (
                  <div className="mt-2.5 flex items-center gap-2 flex-wrap">
                    <button
                      type="button"
                      disabled={isUploadingMemberPhoto}
                      onClick={() => memberPhotoInputRef.current?.click()}
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-bold text-red-700 bg-white hover:bg-red-50 border border-red-200 rounded-lg transition-colors cursor-pointer shadow-2xs disabled:opacity-50"
                      title="Pilih fail imej dari komputer atau telefon untuk tukar foto profil ahli ini"
                    >
                      {isUploadingMemberPhoto ? (
                        <>
                          <Loader2 className="w-3 h-3 animate-spin text-red-600" />
                          <span>Menyimpan...</span>
                        </>
                      ) : (
                        <>
                          <Camera className="w-3 h-3 text-red-600" />
                          <span>Tukar Foto Profil</span>
                        </>
                      )}
                    </button>

                    {selectedMember.photoUrl && selectedMember.photoUrl !== '/default-avatar.svg' && (
                      <button
                        type="button"
                        disabled={isUploadingMemberPhoto}
                        onClick={handleSuperadminResetMemberPhoto}
                        className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-slate-500 hover:text-rose-600 bg-white hover:bg-rose-50 border border-slate-200 hover:border-rose-200 rounded-lg transition-colors cursor-pointer"
                        title="Padam foto dan kembali kepada avatar lalai"
                      >
                        <RotateCcw className="w-3 h-3" />
                        <span>Reset Foto</span>
                      </button>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              {selectedMember.role !== 'Waris' && (
                <div className="p-3 bg-white border border-slate-200 rounded-xl space-y-1.5 col-span-1 sm:col-span-2 overflow-hidden">
                  <div className="flex items-center justify-between">
                    <span className="text-slate-400 font-medium">Peringkat Bengkung & Gelaran Pangkat:</span>
                    <span className="text-xs font-bold text-asmm-red">
                      {getSilatRankFromBengkung(selectedMember.bengkung)}
                    </span>
                  </div>
                  <div className="-mx-3 my-0.5">
                    <BengkungCulaBadge bengkung={selectedMember.bengkung} size="md" fullWidth={true} />
                  </div>
                </div>
              )}

              <div className="p-3 bg-white border border-slate-200 rounded-xl space-y-1">
                <span className="text-slate-400 font-medium">Gelanggang Latihan:</span>
                <div className="font-bold text-slate-900 flex items-center gap-1.5">
                  <MapPin className="w-4 h-4 text-asmm-red" />
                  {selectedMember.gelanggang === 'Semua Gelanggang' ? 'ASMM' : selectedMember.gelanggang}
                </div>
              </div>

              <div className="p-3 bg-white border border-slate-200 rounded-xl space-y-1">
                <span className="text-slate-400 font-medium">Ahli Sejak:</span>
                <div className="font-bold text-slate-900 flex items-center gap-1.5">
                  <Calendar className="w-4 h-4 text-purple-600" />
                  {formatSubmissionDate(selectedMember.joinedDate)}
                </div>
              </div>

              {canViewMemberSensitiveInfo(currentUser, selectedMember) && (
                <>
                  <div className="p-3 bg-white border border-slate-200 rounded-xl space-y-1">
                    <span className="text-slate-400 font-medium">No. Telefon:</span>
                    <div className="font-bold text-slate-900 flex items-center gap-1.5">
                      <Phone className="w-4 h-4 text-emerald-600" />
                      <a href={`https://wa.me/6${selectedMember.phone.replace(/\D/g, '')}`} target="_blank" rel="noreferrer" className="hover:underline">
                        {selectedMember.phone} (WhatsApp)
                      </a>
                    </div>
                  </div>

                  <div className="p-3 bg-white border border-slate-200 rounded-xl space-y-1">
                    <span className="text-slate-400 font-medium">Alamat Emel:</span>
                    <div className="font-bold text-slate-900 flex items-center gap-1.5 truncate">
                      <Mail className="w-4 h-4 text-blue-600" />
                      {selectedMember.email ? (
                        <span>{selectedMember.email}</span>
                      ) : (
                        <span className="text-slate-400 font-normal italic">Belum diisi (Pilihan)</span>
                      )}
                    </div>
                  </div>

                  {selectedMember.address && (
                    <div className="p-3 bg-white border border-slate-200 rounded-xl space-y-1 col-span-1 sm:col-span-2">
                      <span className="text-slate-400 font-medium">Alamat Kediaman:</span>
                      <div className="font-bold text-slate-900 flex items-start gap-1.5">
                        <MapPin className="w-4 h-4 text-asmm-red shrink-0 mt-0.5" />
                        <span>{selectedMember.address}</span>
                      </div>
                    </div>
                  )}

                  {selectedMember.parentId && (
                    <div className="p-3 bg-blue-50 border border-blue-200 rounded-xl space-y-1 col-span-1 sm:col-span-2">
                      <span className="text-blue-700 font-extrabold text-[10px] uppercase tracking-wider block">Pautan Unit Keluarga:</span>
                      <div className="font-bold text-slate-900 text-xs flex items-center gap-2 flex-wrap">
                        <HeartHandshake className="w-4 h-4 text-blue-600 shrink-0" />
                        <span>Hubungan: <strong className="text-blue-800">{selectedMember.relationship || 'Tanggungan'}</strong></span>
                        <span>•</span>
                        <span>Waris / Ketua: <strong className="font-mono text-slate-800">{sanitizeMemberId(selectedMember.parentId)}</strong> {selectedMember.kinName ? `(${selectedMember.kinName})` : ''}</span>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Onboarding Status & Manual Mark for Old Ahli (Strictly visible to management only) */}
            {isManagement && (selectedMember.role === 'Ahli' || selectedMember.role === 'Ahli Seumur Hidup') && (
              <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-2xl flex items-center justify-between gap-3 flex-wrap">
                <div>
                  <span className="text-[10px] font-bold text-slate-400 block uppercase">Status Onboarding</span>
                  <div className="flex items-center gap-2 mt-0.5">
                    {selectedMember.isOldMember || selectedMember.onboardingStatus === 'completed' ? (
                      <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-emerald-100 text-emerald-800 border border-emerald-200 flex items-center gap-1">
                        <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                        <span>Selesai (Ahli Lama / Telah Selesai Onboarding)</span>
                      </span>
                    ) : (
                      <span className="px-2.5 py-0.5 rounded-full text-xs font-bold bg-amber-100 text-amber-800 border border-amber-200 flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5 text-amber-600" />
                        <span>Menunggu Bayaran Pakej Onboarding</span>
                      </span>
                    )}
                  </div>
                </div>

                {isExecutive && !(selectedMember.isOldMember || selectedMember.onboardingStatus === 'completed') && (
                  <button
                    type="button"
                    onClick={async () => {
                      const updated: UserProfile = {
                        ...selectedMember,
                        isOldMember: true,
                        onboardingStatus: 'completed'
                      };
                      setMembers(prev => prev.map(m => m.id === updated.id ? updated : m));
                      setSelectedMember(updated);
                      await saveMemberDB(updated);
                      await refreshMembers();
                      showToast('success', 'Status Onboarding Disahkan', `${selectedMember.name} ditandakan sebagai ahli lama yang telah selesai onboarding.`);
                    }}
                    className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-xs font-bold transition-all shadow-xs flex items-center gap-1.5 cursor-pointer border-none"
                  >
                    <CheckCircle2 className="w-3.5 h-3.5" />
                    <span>Tandakan Selesai (Ahli Lama)</span>
                  </button>
                )}
              </div>
            )}

            <div className="flex items-center justify-between pt-3 border-t border-slate-100">
              {isExecutive ? (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setEditingMember(selectedMember);
                      setSelectedMember(null);
                    }}
                    className="px-3.5 py-2 bg-slate-900 hover:bg-slate-800 text-white font-bold text-xs rounded-xl transition-all flex items-center gap-1.5"
                  >
                    <Edit3 className="w-3.5 h-3.5" />
                    <span>Kemaskini Rekod</span>
                  </button>

                  <button
                    onClick={() => handleDeleteMember(selectedMember.id, selectedMember.name)}
                    className="px-3 py-2 bg-rose-50 text-rose-700 hover:bg-rose-100 font-bold text-xs rounded-xl transition-all border border-rose-200 flex items-center gap-1.5"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                    <span>Padam</span>
                  </button>
                </div>
              ) : <div />}

              <button
                onClick={() => setSelectedMember(null)}
                className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs rounded-xl"
              >
                Tutup
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* EDIT MEMBER MODAL */}
      {editingMember && (
        <Modal
          isOpen={!!editingMember}
          onClose={() => setEditingMember(null)}
          title="Kemaskini Rekod Pesilat"
        >
          <form onSubmit={handleUpdateMember} className="space-y-4">
            <div className="text-[11px] font-mono text-slate-500 flex items-center gap-1.5">
              <span>ID Ahli:</span>
              <span className="font-bold text-slate-800 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                {sanitizeMemberId(editingMember.id)}
              </span>
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Nama Penuh Pesilat</label>
              <input
                type="text"
                required
                value={editingMember.name}
                onChange={(e) => setEditingMember({ ...editingMember, name: e.target.value })}
                className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs font-bold text-slate-900"
              />
            </div>

            <div>
              <div className="flex items-center justify-between mb-1">
                <label className="block text-xs font-bold text-slate-700">No. Kad Pengenalan (IC)</label>
                {canSuperadminEditIc && (
                  <span className="text-[10px] font-bold text-red-600 bg-red-50 border border-red-200 px-2 py-0.5 rounded-full flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3 text-red-600" />
                    <span>Superadmin Boleh Ubah</span>
                  </span>
                )}
              </div>
              <input
                type="text"
                disabled={!canSuperadminEditIc}
                value={editingMember.icNo || ''}
                onChange={(e) => setEditingMember({ ...editingMember, icNo: e.target.value })}
                placeholder="Contoh: 850612025431"
                className={`w-full px-3.5 py-2 rounded-xl text-xs font-mono font-bold transition-all ${
                  canSuperadminEditIc
                    ? 'bg-white border border-red-200 focus:border-red-500 focus:ring-1 focus:ring-red-500 text-slate-900 shadow-2xs'
                    : 'bg-slate-100 border border-slate-200 text-slate-500 cursor-not-allowed'
                }`}
              />
              <p className="text-[10px] text-slate-400 mt-1">
                {canSuperadminEditIc
                  ? 'Format 12 digit tanpa sengkang (-). Hanya Superadmin dibenarkan menukar No. IC pesilat.'
                  : 'Format 12 digit nombor kad pengenalan rasmi.'}
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Peranan Governance</label>
                <select
                  value={editingMember.role}
                  onChange={(e) => setEditingMember({ ...editingMember, role: e.target.value as Role })}
                  className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs font-bold text-slate-900"
                >
                  <option value="Ahli">Ahli</option>
                  <option value="AJK">AJK</option>
                  <option value="Ketua Gelanggang">Ketua Gelanggang</option>
                  <option value="Setiausaha">Setiausaha</option>
                  <option value="Pen. Setiausaha">Pen. Setiausaha</option>
                  <option value="Bendahari">Bendahari</option>
                  <option value="Timbalan Pengerusi">Timbalan Pengerusi</option>
                  <option value="Pengerusi">Pengerusi</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Gelanggang Latihan</label>
                <select
                  value={editingMember.gelanggang === 'Semua Gelanggang' ? 'ASMM' : editingMember.gelanggang}
                  onChange={(e) => setEditingMember({ ...editingMember, gelanggang: e.target.value as Gelanggang })}
                  className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs font-bold text-slate-900"
                >
                  <option value="Pusaka Warisan">Pusaka Warisan</option>
                  <option value="Wadi AnNour">Wadi AnNour</option>
                  <option value="Paya Besar">Paya Besar</option>
                  <option value="ASMM">ASMM</option>
                </select>
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Peringkat Bengkung</label>
              <select
                value={getResolvedBengkungValue(editingMember.bengkung, bengkungRanksList)}
                onChange={(e) => setEditingMember({ ...editingMember, bengkung: e.target.value as BengkungLevel })}
                className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs font-bold text-slate-900"
              >
                {bengkungRanksList.map((rank) => {
                  const optVal = getFormattedBengkungOption(rank);
                  return (
                    <option key={rank.code} value={optVal}>
                      {optVal}
                    </option>
                  );
                })}
                {editingMember.bengkung && !bengkungRanksList.some(r => getFormattedBengkungOption(r) === getResolvedBengkungValue(editingMember.bengkung, bengkungRanksList)) && (
                  <option value={getResolvedBengkungValue(editingMember.bengkung, bengkungRanksList)}>
                    {getResolvedBengkungValue(editingMember.bengkung, bengkungRanksList)}
                  </option>
                )}
              </select>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">No. Telefon</label>
                <input
                  type="text"
                  value={editingMember.phone || ''}
                  onChange={(e) => setEditingMember({ ...editingMember, phone: e.target.value })}
                  placeholder="019-4920194"
                  className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs font-bold text-slate-900"
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-slate-700 mb-1">Alamat Emel</label>
                <input
                  type="email"
                  value={editingMember.email || ''}
                  onChange={(e) => setEditingMember({ ...editingMember, email: e.target.value })}
                  placeholder="pesilat@gmail.com"
                  className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs font-bold text-slate-900"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Alamat Kediaman</label>
              <textarea
                rows={2}
                value={editingMember.address || ''}
                onChange={(e) => setEditingMember({ ...editingMember, address: e.target.value })}
                placeholder="Alamat penuh kediaman pesilat..."
                className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs font-bold text-slate-900 resize-none"
              />
            </div>

            <div>
              <label className="block text-xs font-bold text-slate-700 mb-1">Tarikh Pendaftaran (Ahli Sejak)</label>
              <input
                type="date"
                required
                value={editingMember.joinedDate || new Date().toISOString().split('T')[0]}
                onChange={(e) => setEditingMember({ ...editingMember, joinedDate: e.target.value })}
                className="w-full px-3.5 py-2 rounded-xl bg-slate-50 border border-slate-200 text-xs font-bold text-slate-900 font-mono"
              />
            </div>

            <div className="flex justify-end gap-3 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setEditingMember(null)}
                className="px-4 py-2 text-xs font-semibold text-slate-600 hover:text-slate-900"
              >
                Batal
              </button>
              <button
                type="submit"
                className="px-5 py-2 bg-asmm-red text-white font-bold text-xs rounded-xl hover:bg-asmm-crimson transition-all flex items-center gap-1.5"
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span>Simpan Perubahan</span>
              </button>
            </div>
          </form>
        </Modal>
      )}



      {/* DELETE CONFIRMATION MODAL */}
      {confirmDeleteMember && (
        <Modal
          isOpen={!!confirmDeleteMember}
          onClose={() => setConfirmDeleteMember(null)}
          title={`Sahkan Pemadaman Rekod ${confirmDeleteMember.role === 'Waris' || deleteTargetFamilyMembers.length > 0 ? 'Waris' : 'Ahli'}`}
          maxWidth="max-w-xl"
        >
          <div className="space-y-4">
            {/* Warning Alert Banner */}
            <div className="p-3.5 bg-rose-50 border border-rose-200 rounded-2xl flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
              <div className="text-xs text-rose-900 space-y-1">
                <p className="font-bold">
                  Tindakan ini adalah kekal dan tidak boleh diundur.
                </p>
                <p className="text-rose-700 leading-relaxed">
                  Semua invois, rekod log, pendaftaran latihan / ujian bengkung, pesanan barangan, dan transaksi kewangan bagi ahli ini akan dipadamkan daripada pangkalan data.
                </p>
              </div>
            </div>

            {/* Target Member Summary Card */}
            <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-2xl flex items-center gap-3.5">
              <img
                src={confirmDeleteMember.photoUrl || '/default-avatar.svg'}
                alt={confirmDeleteMember.name}
                onError={(e) => { (e.target as HTMLElement).setAttribute('src', '/default-avatar.svg'); }}
                className="w-12 h-12 rounded-xl object-cover border border-slate-200 shrink-0"
              />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] px-2 py-0.5 rounded border uppercase tracking-wider ${getRoleBadgeStyle(confirmDeleteMember.role)}`}>
                    {confirmDeleteMember.role}
                  </span>
                  <span className="text-[10px] font-mono font-bold text-slate-600 bg-white px-1.5 py-0.5 rounded border border-slate-200">
                    {sanitizeMemberId(confirmDeleteMember.id)}
                  </span>
                  {confirmDeleteMember.gelanggang && (
                    <span className="text-[10px] text-slate-500 font-semibold">
                      • {confirmDeleteMember.gelanggang}
                    </span>
                  )}
                </div>
                <h4 className="text-sm font-black text-slate-900 truncate mt-1">
                  {confirmDeleteMember.name}
                </h4>
                <div className="text-[11px] text-slate-500 font-medium flex items-center gap-2 mt-0.5">
                  {confirmDeleteMember.icNo && <span>IC: {confirmDeleteMember.icNo}</span>}
                  {confirmDeleteMember.phone && <span>• Tel: {confirmDeleteMember.phone}</span>}
                </div>
              </div>
            </div>

            {/* If Target is a Waris or Has Dependents */}
            {(confirmDeleteMember.role === 'Waris' || deleteTargetFamilyMembers.length > 0) && (
              <div className="space-y-3">
                {deleteTargetFamilyMembers.length > 0 ? (
                  <div className="p-3.5 bg-amber-50/70 border border-amber-200 rounded-2xl space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-black text-amber-950 flex items-center gap-1.5">
                        <Users className="w-3.5 h-3.5 text-amber-700" />
                        <span>Ahli / Pesilat Tanggungan ({deleteTargetFamilyMembers.length} Orang)</span>
                      </span>
                    </div>
                    <div className="space-y-1.5 max-h-36 overflow-y-auto pr-1">
                      {deleteTargetFamilyMembers.map(child => (
                        <div key={child.id} className="flex items-center justify-between p-2 bg-white rounded-xl border border-amber-200/80 text-xs">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className="w-2 h-2 rounded-full bg-amber-500 shrink-0" />
                            <span className="font-bold text-slate-900 truncate">{child.name}</span>
                            <span className="font-mono text-[10px] text-slate-500 shrink-0">({sanitizeMemberId(child.id)})</span>
                          </div>
                          <span className="text-[10px] font-semibold text-slate-600 shrink-0 bg-slate-50 px-2 py-0.5 rounded border border-slate-200">
                            {child.gelanggang || 'Gelanggang'}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="p-3 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-600">
                    Akaun ini berdaftar sebagai peranan Waris tetapi tiada pesilat tanggungan dipautkan.
                  </div>
                )}

                {/* Superadmin Checkbox Option */}
                {isSuperAdmin(currentUser) && deleteTargetFamilyMembers.length > 0 ? (
                  <div className="p-4 bg-white border-2 border-rose-200 rounded-2xl space-y-2.5 shadow-2xs">
                    <label className="flex items-start gap-3 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={deleteWholeFamily}
                        onChange={(e) => setDeleteWholeFamily(e.target.checked)}
                        className="w-4 h-4 mt-0.5 text-rose-600 rounded border-slate-300 focus:ring-rose-500 cursor-pointer"
                      />
                      <div className="space-y-1 flex-1">
                        <span className="text-xs font-black text-slate-900 block">
                          Padam Seluruh Keluarga & Semua Tanggungan ({deleteTargetFamilyMembers.length} orang ahli)
                        </span>
                        <p className="text-[11px] leading-relaxed font-medium">
                          {deleteWholeFamily ? (
                            <span className="text-rose-700 font-bold block">
                              Perhatian: Semua {deleteTargetFamilyMembers.length} orang ahli tanggungan berserta akaun waris ({confirmDeleteMember.name}) dan semua invois serta rekod mereka akan dipadamkan secara menyeluruh.
                            </span>
                          ) : (
                            <span className="text-slate-500 block">
                              Kotak tidak ditandakan: Hanya akaun waris ({confirmDeleteMember.name}) yang dipadam. Ahli tanggungan akan dikekalkan sebagai akaun pesilat bebas.
                            </span>
                          )}
                        </p>
                      </div>
                    </label>
                  </div>
                ) : (
                  deleteTargetFamilyMembers.length > 0 && (
                    <p className="text-[11px] text-slate-500 bg-slate-50 p-2.5 rounded-xl border border-slate-200">
                      Hanya akaun waris ({confirmDeleteMember.name}) akan dipadam. Ahli tanggungan akan dikekalkan di pangkalan data.
                    </p>
                  )
                )}
              </div>
            )}

            {/* Non-waris notice */}
            {confirmDeleteMember.role !== 'Waris' && deleteTargetFamilyMembers.length === 0 && (
              <p className="text-xs text-slate-600 bg-slate-50 p-3 rounded-xl border border-slate-200 leading-relaxed">
                Semua rekod pendaftaran, invois, rekod audit kelulusan, dan transaksi kewangan bagi pesilat <strong className="text-slate-900">{confirmDeleteMember.name}</strong> akan dipadamkan secara bersih.
              </p>
            )}

            {/* Modal Actions */}
            <div className="flex justify-end gap-2.5 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setConfirmDeleteMember(null)}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded-xl cursor-pointer border-none transition-colors"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={confirmDelete}
                disabled={isDeleting}
                className="px-5 py-2 bg-rose-600 hover:bg-rose-700 text-white font-black text-xs rounded-xl transition-all shadow-sm flex items-center gap-1.5 cursor-pointer border-none disabled:opacity-50 active:scale-95"
              >
                <Trash2 className={`w-3.5 h-3.5 ${isDeleting ? 'animate-spin' : ''}`} />
                <span>
                  {isDeleting
                    ? 'Memadam...'
                    : deleteWholeFamily && deleteTargetFamilyMembers.length > 0
                      ? `Padam Seluruh Keluarga (${1 + deleteTargetFamilyMembers.length} Ahli)`
                      : 'Sahkan Padam Rekod'}
                </span>
              </button>
            </div>
          </div>
        </Modal>
      )}

      {/* PENDING MEMBER FULL DETAIL REVIEW MODAL */}
      {previewPendingMember && (
        <Modal
          isOpen={!!previewPendingMember}
          onClose={() => setPreviewPendingMember(null)}
          title="Semakan Permohonan Pendaftaran Ahli"
          maxWidth="max-w-2xl"
        >
          <div className="space-y-5">
            {/* Top Profile Summary */}
            <div className={`flex items-center gap-4 p-4 rounded-2xl border ${previewPendingMember.status === 'Ditolak'
              ? 'bg-gradient-to-r from-rose-50/90 via-slate-50 to-white border-rose-200/80'
              : previewPendingMember.status === 'Aktif'
                ? 'bg-gradient-to-r from-emerald-50/90 via-slate-50 to-white border-emerald-200/80'
                : 'bg-gradient-to-r from-amber-50/90 via-slate-50 to-white border-amber-200/80'
              }`}>
              <img
                src={previewPendingMember.photoUrl || '/default-avatar.svg'}
                alt={previewPendingMember.name}
                onError={(e) => { (e.target as HTMLElement).setAttribute('src', '/default-avatar.svg'); }}
                className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl object-cover border border-slate-200 shadow-sm shrink-0"
              />
              <div className="min-w-0 flex-1 space-y-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-md bg-slate-900 text-white">
                    {previewPendingMember.role || 'Ahli'}
                  </span>
                  {previewPendingMember.status === 'Aktif' ? (
                    <span className="text-[10px] font-extrabold text-emerald-800 bg-emerald-100/90 px-2.5 py-0.5 rounded-md border border-emerald-200 flex items-center gap-1">
                      <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                      <span>Telah Diluluskan</span>
                    </span>
                  ) : previewPendingMember.status === 'Ditolak' ? (
                    <span className="text-[10px] font-extrabold text-rose-800 bg-rose-100/90 px-2.5 py-0.5 rounded-md border border-rose-200 flex items-center gap-1">
                      <UserX className="w-3 h-3 text-rose-600" />
                      <span>Permohonan Ditolak</span>
                    </span>
                  ) : (
                    <span className="text-[10px] font-extrabold text-amber-800 bg-amber-100/90 px-2.5 py-0.5 rounded-md border border-amber-200 flex items-center gap-1">
                      <Clock className="w-3 h-3 text-amber-600 animate-pulse" />
                      <span>Menunggu Kelulusan</span>
                    </span>
                  )}
                </div>
                <h3 className="text-base font-black text-slate-900 mt-1">
                  {previewPendingMember.name}
                </h3>
                <div className="text-xs text-slate-500 font-mono mt-1 flex items-center gap-1.5 flex-wrap">
                  <span className="font-bold text-slate-800 bg-slate-100 px-2 py-0.5 rounded border border-slate-200">
                    ID: {sanitizeMemberId(previewPendingMember.id)}
                  </span>
                  {renderIcNo(previewPendingMember) && (
                    <span>• IC: {renderIcNo(previewPendingMember)}</span>
                  )}
                </div>

                {/* Audit Reviewer Info Banner */}
                {previewPendingMember.approvedByName && (
                  <div className="text-[11px] font-bold text-emerald-800 bg-emerald-50/80 px-2.5 py-1 rounded-lg border border-emerald-200/80 flex items-center gap-1.5 mt-1">
                    <ShieldCheck className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <span>Diluluskan oleh: <strong className="text-emerald-950">{previewPendingMember.approvedByName}</strong> ({formatDateTime(previewPendingMember.approvedAt)})</span>
                  </div>
                )}
                {previewPendingMember.rejectedByName && (
                  <div className="text-[11px] font-bold text-rose-800 bg-rose-50/80 px-2.5 py-1 rounded-lg border border-rose-200/80 flex items-center gap-1.5 mt-1">
                    <UserX className="w-3.5 h-3.5 text-rose-600 shrink-0" />
                    <span>Ditolak oleh: <strong className="text-rose-950">{previewPendingMember.rejectedByName}</strong> ({formatDateTime(previewPendingMember.rejectedAt)})</span>
                  </div>
                )}
              </div>
            </div>

            {/* Structured Info Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-xs">
              {/* Gelanggang */}
              <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200 space-y-1">
                <span className="text-slate-400 font-medium">Gelanggang Latihan:</span>
                <div className="font-extrabold text-slate-900 flex items-center gap-1.5 text-xs">
                  <MapPin className="w-4 h-4 text-asmm-red shrink-0" />
                  <span>{(previewPendingMember.gelanggang === 'Semua Gelanggang' ? 'ASMM' : previewPendingMember.gelanggang) || 'Belum Ditetapkan'}</span>
                </div>
              </div>

              {/* Sesi Latihan */}
              <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200 space-y-1">
                <span className="text-slate-400 font-medium">Sesi Latihan:</span>
                <div className="font-extrabold text-slate-900 flex items-center gap-1.5 text-xs">
                  <Clock className="w-4 h-4 text-blue-600 shrink-0" />
                  <span>{previewPendingMember.sessionTime || 'Semua Sesi'}</span>
                </div>
              </div>

              {/* Bengkung */}
              <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200 space-y-1">
                <span className="text-slate-400 font-medium">Peringkat Silat:</span>
                <div className="font-extrabold text-slate-900 flex items-center gap-1.5 text-xs">
                  <ShieldCheck className="w-4 h-4 text-emerald-600 shrink-0" />
                  <span>{getSilatRankFromBengkung(previewPendingMember.bengkung || 'Putih')}</span>
                </div>
              </div>

              {/* Tarikh Permohonan */}
              <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200 space-y-1">
                <span className="text-slate-400 font-medium">Tarikh Permohonan:</span>
                <div className="font-extrabold text-slate-900 flex items-center gap-1.5 text-xs">
                  <Calendar className="w-4 h-4 text-purple-600 shrink-0" />
                  <span>{formatSubmissionDate(previewPendingMember.joinedDate)}</span>
                </div>
              </div>

              {canViewMemberSensitiveInfo(currentUser, previewPendingMember) && (
                <>
                  {/* Telefon & WhatsApp */}
                  <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200 space-y-1.5">
                    <span className="text-slate-400 font-medium">No. Telefon:</span>
                    <div className="flex items-center gap-2">
                      <span className="font-mono font-bold text-slate-900">{previewPendingMember.phone || '-'}</span>
                      {previewPendingMember.phone && (
                        <a
                          href={`https://wa.me/6${previewPendingMember.phone.replace(/\D/g, '')}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-100/90 hover:bg-emerald-200 px-2 py-0.5 rounded-lg font-bold text-[11px] transition-colors"
                        >
                          <MessageCircle className="w-3 h-3" />
                          <span>WhatsApp</span>
                        </a>
                      )}
                    </div>
                  </div>

                  {/* Emel */}
                  <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200 space-y-1">
                    <span className="text-slate-400 font-medium">Alamat Emel:</span>
                    <div className="font-bold text-slate-900 flex items-center gap-1.5 truncate">
                      <Mail className="w-4 h-4 text-blue-600 shrink-0" />
                      <span className="truncate">{previewPendingMember.email || '-'}</span>
                    </div>
                  </div>

                  {/* Alamat Penuh */}
                  <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200 space-y-1.5 col-span-1 sm:col-span-2">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400 font-medium">Alamat Kediaman:</span>
                      {previewPendingMember.address && (
                        <a
                          href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(previewPendingMember.address)}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-blue-600 hover:text-blue-800 font-bold inline-flex items-center gap-1 text-xs"
                        >
                          <Navigation className="w-3.5 h-3.5" />
                          <span>Buka di Peta (Maps)</span>
                        </a>
                      )}
                    </div>
                    <p className="font-bold text-slate-900 bg-white p-2.5 rounded-xl border border-slate-200 leading-relaxed">
                      {previewPendingMember.address || 'Tiada alamat dinyatakan'}
                    </p>
                  </div>

                  {/* Maklumat Waris / Penjaga if any */}
                  {(previewPendingMember.kinName || previewPendingMember.kinPhone) && (
                    <div className="p-3.5 bg-slate-50 rounded-2xl border border-slate-200 space-y-1.5 col-span-1 sm:col-span-2">
                      <span className="text-slate-400 font-medium">Maklumat Waris / Penjaga:</span>
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 bg-white p-2.5 rounded-xl border border-slate-200">
                        <div>
                          <span className="font-black text-slate-900 text-xs block">{previewPendingMember.kinName || 'Waris'}</span>
                          <span className="text-[11px] text-slate-500 font-medium">{previewPendingMember.relationship || 'Penjaga'}</span>
                        </div>
                        {previewPendingMember.kinPhone && (
                          <a
                            href={`https://wa.me/6${previewPendingMember.kinPhone.replace(/\D/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1.5 px-3 py-1 bg-emerald-500 hover:bg-emerald-600 text-white rounded-xl text-xs font-bold transition-all shrink-0"
                          >
                            <MessageCircle className="w-3 h-3" />
                            <span>WhatsApp Waris</span>
                          </a>
                        )}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Modal Actions */}
            <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setPreviewPendingMember(null)}
                className="w-full sm:w-auto px-4 py-2.5 text-xs font-bold text-slate-600 hover:text-slate-900 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors cursor-pointer border-none order-2 sm:order-1"
              >
                Tutup
              </button>

              <div className="flex items-center gap-2 w-full sm:w-auto justify-end order-1 sm:order-2">
                {previewPendingMember.status === 'Menunggu Pengesahan' && (
                  <>
                    <button
                      type="button"
                      onClick={() => {
                        const m = previewPendingMember;
                        handleReject(m.id, m.name);
                      }}
                      className="px-4 py-2.5 bg-rose-50 hover:bg-rose-100 text-rose-700 font-bold text-xs rounded-xl border border-rose-200 transition-colors flex items-center gap-1.5 cursor-pointer"
                    >
                      <UserX className="w-3.5 h-3.5" />
                      <span>Tolak Permohonan</span>
                    </button>
                    <button
                      type="button"
                      onClick={() => handleApprove(previewPendingMember)}
                      className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs rounded-xl shadow-sm transition-all flex items-center gap-1.5 cursor-pointer border-none active:scale-95"
                    >
                      <CheckCircle2 className="w-4 h-4" />
                      <span>Luluskan Keahlian Sekarang</span>
                    </button>
                  </>
                )}

                {previewPendingMember.status === 'Ditolak' && (
                  <button
                    type="button"
                    onClick={() => handleApprove(previewPendingMember)}
                    className="px-5 py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white font-black text-xs rounded-xl shadow-sm transition-all flex items-center gap-1.5 cursor-pointer border-none active:scale-95"
                  >
                    <RotateCcw className="w-4 h-4" />
                    <span>Luluskan Semula Keahlian</span>
                  </button>
                )}

                {isSuperAdmin(currentUser) && (
                  <button
                    type="button"
                    onClick={() => {
                      const m = previewPendingMember;
                      setPreviewPendingMember(null);
                      handleDeleteMember(m.id, m.name);
                    }}
                    className="px-4 py-2.5 bg-rose-50 hover:bg-rose-100 text-rose-700 font-bold text-xs rounded-xl border border-rose-200 transition-colors flex items-center gap-1.5 cursor-pointer"
                    title="Padam rekod secara kekal (Superadmin sahaja)"
                  >
                    <Trash2 className="w-3.5 h-3.5 text-rose-600" />
                    <span>Padam Rekod</span>
                  </button>
                )}
              </div>
            </div>
          </div>
        </Modal>
      )}

      {/* BULK REJECT CONFIRMATION MODAL */}
      {showBulkRejectModal && (
        <Modal
          isOpen={showBulkRejectModal}
          onClose={() => setShowBulkRejectModal(false)}
          title="Sahkan Penolakan Berkelompok"
        >
          <div className="space-y-4">
            <div className="p-3.5 bg-rose-50 border border-rose-200 rounded-2xl flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-rose-600 shrink-0 mt-0.5" />
              <div className="text-xs text-rose-900 space-y-1">
                <p className="font-bold">
                  Adakah anda pasti ingin menolak <span className="underline font-black">{selectedPendingIds.length} permohonan pendaftaran</span> yang dipilih?
                </p>
                <p className="text-rose-700">
                  Rekod permohonan ini akan disimpan dengan status 'Ditolak' dalam pangkalan data dan log audit rasmi, dan boleh disemak atau diluluskan semula pada bila-bila masa.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-2.5 pt-3 border-t border-slate-100">
              <button
                type="button"
                onClick={() => setShowBulkRejectModal(false)}
                className="px-4 py-2 text-xs font-bold text-slate-600 hover:text-slate-900 bg-slate-100 rounded-xl cursor-pointer border-none"
              >
                Batal
              </button>
              <button
                type="button"
                onClick={handleBulkReject}
                disabled={isBulkRejecting}
                className="px-5 py-2 bg-rose-600 hover:bg-rose-700 text-white font-black text-xs rounded-xl transition-all shadow-sm flex items-center gap-1.5 cursor-pointer border-none disabled:opacity-50"
              >
                <UserX className="w-3.5 h-3.5" />
                <span>{isBulkRejecting ? 'Memproses...' : `Sahkan Tolak (${selectedPendingIds.length})`}</span>
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
};
